import type { FormEvent, PointerEvent as ReactPointerEvent } from 'react';
import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import {
  BottomNav,
  EmptyState,
  ScreenHeader,
  Sheet,
  Toast,
  type AppView,
} from './components/Ui';
import { supabase } from './lib/supabase';
import { buildInviteUrl, inviteShareText } from './lib/inviteLink';
import { formatItalianPhone, memberLabel } from './lib/inviteAccess';
import {
  LongPressController,
  canModifySetlist,
  filterCollaborators,
  filterSongs,
  moveDestination,
  recordRecentCollaborator,
  runSequentially,
  songContextActions,
  splitRecentCollaborators,
  transitionWorkspaceUi,
  unreadTransferItemCount,
  youtubeVideoId,
  filterAndSortInbox,
  groupInboxByDay,
  type InboxSort,
  type WorkspacePanel,
  type WorkspaceUiAction,
} from './lib/workspaceBehavior';
import {
  alternativeForItem,
  branchForItem,
  branchFromOrigin,
  chainBetween,
  chainForItem,
  createAlternative,
  createChain,
  emptyStructures,
  removeAlternative,
  removeBranch,
  removeChain,
  type SetlistStructures,
} from './lib/setlistStructures';

export type Setlist = {
  id: string;
  title: string;
  group_name: string;
  created_at?: string;
  updated_at?: string;
  proposal_ids?: string[];
};
export type Song = {
  id: string;
  title: string;
  author: string | null;
  source_url: string | null;
};
export type Item = {
  id: string;
  song_id: string | null;
  title: string;
  author: string | null;
  source_url: string | null;
  position: number;
  block_id: string | null;
  block_ordinal: number | null;
  item_kind?: 'song' | 'text';
  notes?: string | null;
  is_proposal?: boolean;
  proposed_by?: string | null;
};
export type Collaborator = {
  profile_id: string;
  display_name: string;
  instrument_role: string;
  phone: string;
};
export type Transfer = {
  id: string;
  status: string;
  created_at?: string | null;
  sender_id?: string | null;
  sender_phone?: string | null;
  sender_instrument?: string | null;
  origin?: 'collaborator' | 'self_import' | string | null;
  items: { title: string; author: string | null; ordinal: number }[];
};
export type PendingInvite = { id: string; expires_at: string };
export type WorkspaceState = {
  private_profile: { phone: string | null } | null;
  songs: Song[];
  items: Item[];
  collaborators: Collaborator[];
  transfers: Transfer[];
  pending_invites: PendingInvite[];
  structures?: SetlistStructures;
};
type SheetName = WorkspacePanel;
type ConfirmState = {
  title: string;
  copy: string;
  action: () => Promise<void>;
  danger?: boolean;
  confirmLabel?: string;
  cancelLabel?: string;
};

function VisualIcon({
  name,
}: {
  name: 'play' | 'alternative' | 'chain' | 'trash' | 'edit' | 'move' | 'branch' | 'link';
}) {
  const paths = {
    play: <path d="m9 7 7 5-7 5V7Z" fill="currentColor" />,
    alternative: (
      <>
        <path d="M6 4v5c0 2 2 3 4 3h8" />
        <path d="M6 20v-5c0-2 2-3 4-3" />
        <path d="m15 9 3 3-3 3" />
      </>
    ),
    chain: (
      <>
        <path d="m9 15-2 2a3 3 0 0 1-4-4l3-3a3 3 0 0 1 4 0" />
        <path d="m15 9 2-2a3 3 0 0 1 4 4l-3 3a3 3 0 0 1-4 0" />
        <path d="m8 16 8-8" />
      </>
    ),
    trash: (
      <>
        <path d="M5 7h14M9 7V4h6v3M8 10v8M12 10v8M16 10v8M7 7l1 14h8l1-14" />
      </>
    ),
    edit: (
      <>
        <path d="m4 20 4-1 11-11-3-3L5 16l-1 4Z" />
        <path d="m14 7 3 3" />
      </>
    ),
    move: (
      <>
        <path d="M12 3v18M8 7l4-4 4 4M8 17l4 4 4-4" />
      </>
    ),
    branch: (
      <>
        <path d="M6 4v5c0 2 2 3 4 3h8" />
        <path d="M6 20v-5c0-2 2-3 4-3" />
        <path d="m15 9 3 3-3 3" />
      </>
    ),
    link: (
      <>
        <path d="M10 14 14 10" />
        <path d="m8 16-2 2a3 3 0 0 1-4-4l3-3a3 3 0 0 1 4 0" />
        <path d="m16 8 2-2a3 3 0 0 1 4 4l-3 3a3 3 0 0 1-4 0" />
      </>
    ),
  };
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      {paths[name]}
    </svg>
  );
}

export function Workspace({
  profile,
  setlists,
  onRefresh,
  onLogout,
  visualState,
  visualView,
  visualPanel,
  demoMode = false,
  demoRpc,
}: {
  profile: { display_name: string; instrument_role: string; phone: string | null };
  setlists: Setlist[];
  onRefresh: () => Promise<void>;
  onLogout: () => Promise<void>;
  visualState?: WorkspaceState;
  visualView?: AppView;
  visualPanel?: SheetName;
  demoMode?: boolean;
  demoRpc?: (
    name: string,
    args: Record<string, unknown>,
  ) => Promise<{ data: unknown; error: string | null }>;
}) {
  const [view, setView] = useState<AppView>(visualView ?? 'home');
  const [selected, setSelected] = useState(setlists[0]?.id ?? '');
  const [state, setState] = useState<WorkspaceState>(
    visualState ?? {
      private_profile: null,
      songs: [],
      items: [],
      collaborators: [],
      transfers: [],
      pending_invites: [],
    },
  );
  const [sheet, setSheet] = useState<SheetName>(visualPanel ?? null);
  const [message, setMessage] = useState<{ text: string; error?: boolean } | null>(null);
  const [checked, setChecked] = useState<string[]>([]);
  const [libraryQuery, setLibraryQuery] = useState('');
  const [libraryReturnSetlistId, setLibraryReturnSetlistId] = useState('');
  const [hiddenNotesSetlistIds, setHiddenNotesSetlistIds] = useState<Set<string>>(() => {
    try {
      return new Set(JSON.parse(localStorage.getItem('scaletta:hidden-notes') ?? '[]'));
    } catch {
      return new Set();
    }
  });
  const [readTransferIds, setReadTransferIds] = useState<Set<string>>(() => {
    try {
      return new Set(JSON.parse(localStorage.getItem('scaletta:read-transfers') ?? '[]'));
    } catch {
      return new Set();
    }
  });
  const [readProposalIds, setReadProposalIds] = useState<Set<string>>(() => {
    try {
      return new Set(JSON.parse(localStorage.getItem('scaletta:read-proposals') ?? '[]'));
    } catch {
      return new Set();
    }
  });
  const [inboxSenderId, setInboxSenderId] = useState('');
  const [inboxSort, setInboxSort] = useState<InboxSort>('newest');
  const [inboxCollaboratorQuery, setInboxCollaboratorQuery] = useState('');
  const [recentCollaboratorIds, setRecentCollaboratorIds] = useState<string[]>(() => {
    try {
      return JSON.parse(localStorage.getItem('scaletta:recent-collaborators') ?? '[]');
    } catch {
      return [];
    }
  });
  const [activeSong, setActiveSong] = useState<Song | null>(null);
  const [songNeedsDestination, setSongNeedsDestination] = useState(false);
  const [highlightedSong, setHighlightedSong] = useState('');
  const [highlightedItem, setHighlightedItem] = useState('');
  const [confirmation, setConfirmation] = useState<ConfirmState | null>(null);
  const [activeItem, setActiveItem] = useState<Item | null>(
    visualPanel === 'actions' || visualPanel === 'item-form'
      ? (visualState?.items[0] ?? null)
      : null,
  );
  const [moveItem, setMoveItem] = useState<Item | null>(null);
  const structures = state.structures ?? emptyStructures();
  const [draggingMove, setDraggingMove] = useState(false);
  const [dropIndex, setDropIndex] = useState<number | null>(null);
  const [dropEdge, setDropEdge] = useState<{
    itemIndex: number;
    side: 'before' | 'after';
  } | null>(null);
  const [dragOffsetY, setDragOffsetY] = useState(0);
  const draggingMoveRef = useRef(false);
  const dragOriginRef = useRef<{
    pointerY: number;
    top: number;
    height: number;
    scrollTop: number;
  } | null>(null);
  const dragPointerYRef = useRef<number | null>(null);
  const autoScrollFrameRef = useRef<number | null>(null);
  const songListRef = useRef<HTMLDivElement | null>(null);
  const lastCallError = useRef('');
  const longPress = useRef<LongPressController | null>(null);
  if (!longPress.current) longPress.current = new LongPressController();
  const currentSetlist = setlists.find((entry) => entry.id === selected);
  const notesVisible = !hiddenNotesSetlistIds.has(selected);
  const toggleNotesVisibility = () => {
    setHiddenNotesSetlistIds((current) => {
      const next = new Set(current);
      if (next.has(selected)) next.delete(selected);
      else next.add(selected);
      localStorage.setItem('scaletta:hidden-notes', JSON.stringify([...next]));
      return next;
    });
  };
  const canEdit = canModifySetlist(selected);
  const pending = state.transfers.filter((transfer) => transfer.status === 'pending');
  const inboxCollaborators = filterCollaborators(
    state.collaborators,
    inboxCollaboratorQuery,
  );
  const inboxGroups = groupInboxByDay(
    filterAndSortInbox(pending, inboxSenderId, inboxSort),
  );
  const unreadItems = unreadTransferItemCount(state.transfers, readTransferIds);
  const proposalCounts = Object.fromEntries(
    setlists.map((entry) => [
      entry.id,
      (entry.proposal_ids ?? []).filter((id) => !readProposalIds.has(id)).length,
    ]),
  );
  const unreadProposals = Object.values(proposalCounts).reduce(
    (total, count) => total + count,
    0,
  );
  const compactBranchIds = new Set(
    structures.branches.flatMap((branch) => [
      branch.mainPathIds[0],
      branch.branchPathIds[0],
    ]),
  );
  const compactAlternativeIds = new Set(
    structures.alternatives.map((relation) => relation.alternativeId),
  );

  const load = useCallback(async () => {
    if (visualState || demoMode) return;
    const { data, error } = await supabase.rpc('get_mvp_state', {
      p_setlist_id: selected || null,
    });
    if (error) setMessage({ text: friendlyError(error.message), error: true });
    else setState(data as WorkspaceState);
  }, [selected, visualState, demoMode]);

  useEffect(() => {
    void load();
  }, [load]);
  useEffect(() => {
    if (demoMode && visualState) setState(visualState);
  }, [demoMode, visualState]);

  useEffect(() => {
    if (view !== 'setlist' || sheet !== 'actions') return;
    const clearSelection = () => window.getSelection()?.removeAllRanges();
    clearSelection();
    document.addEventListener('selectionchange', clearSelection);
    return () => document.removeEventListener('selectionchange', clearSelection);
  }, [view, sheet]);
  useEffect(() => {
    if (!selected && setlists[0]) setSelected(setlists[0].id);
  }, [selected, setlists]);
  useEffect(() => {
    if (!message) return;
    const timer = window.setTimeout(() => setMessage(null), 4200);
    return () => window.clearTimeout(timer);
  }, [message]);

  async function call(name: string, args: Record<string, unknown>) {
    setMessage(null);
    const { data, error } = demoRpc
      ? await demoRpc(name, args)
      : await supabase.rpc(name, args);
    if (error) {
      const text = friendlyError(typeof error === 'string' ? error : error.message);
      lastCallError.current = text;
      setMessage({ text, error: true });
      return null;
    }
    lastCallError.current = '';
    if (!demoMode) await load();
    return { data };
  }

  function applyUiAction(action: WorkspaceUiAction) {
    const next = transitionWorkspaceUi({ view, panel: sheet }, action);
    setView(next.view);
    setSheet(next.panel);
  }

  function navigate(next: AppView, keepLibraryReturn = false) {
    if (next !== 'library' || !keepLibraryReturn) setLibraryReturnSetlistId('');
    applyUiAction({ type: 'navigate', view: next });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function openSetlistAdd() {
    setActiveItem(state.items.at(-1) ?? null);
    setSheet('insert-box');
  }

  function returnFromLibraryToSetlist() {
    if (!libraryReturnSetlistId) return;
    setSelected(libraryReturnSetlistId);
    setChecked([]);
    navigate('setlist');
  }
  function markTransferRead(transferId: string) {
    setReadTransferIds((current) => {
      if (current.has(transferId)) return current;
      const next = new Set(current).add(transferId);
      localStorage.setItem('scaletta:read-transfers', JSON.stringify([...next]));
      return next;
    });
  }
  function reveal(id: string, kind: 'song' | 'item') {
    if (kind === 'song') setHighlightedSong(id);
    else setHighlightedItem(id);
    window.setTimeout(() => {
      document
        .querySelector(`[data-${kind}-id="${id}"]`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 120);
    window.setTimeout(
      () => (kind === 'song' ? setHighlightedSong('') : setHighlightedItem('')),
      1900,
    );
  }
  function confirmAction(state: ConfirmState) {
    setConfirmation(state);
  }
  function openSetlist(id: string) {
    const proposalIds = setlists.find((entry) => entry.id === id)?.proposal_ids ?? [];
    if (proposalIds.length > 0) {
      setReadProposalIds((current) => {
        const next = new Set(current);
        proposalIds.forEach((proposalId) => {
          next.add(proposalId);
        });
        localStorage.setItem('scaletta:read-proposals', JSON.stringify([...next]));
        return next;
      });
    }
    setSelected(id);
    applyUiAction({ type: 'open-setlist' });
  }

  async function invite() {
    if (!selected) return false;
    const result = await call('create_quick_invite', {
      p_setlist_id: selected,
    });
    if (!result) return false;
    if (demoMode) {
      setMessage({ text: 'Invito simulato localmente. Nessun link è stato condiviso.' });
      setSheet(null);
      return true;
    }
    const token = result.data;
    if (typeof token !== 'string') return false;
    const title = currentSetlist?.title ?? 'Scaletta';
    const url = buildInviteUrl(window.location.origin, title, token);
    const text = inviteShareText(title);
    const canShare = typeof navigator.share === 'function';
    try {
      if (canShare)
        await navigator.share({
          title: `Invito a ${title}`,
          text,
          url: url.toString(),
        });
      else await navigator.clipboard.writeText(`${text}\n${url.toString()}`);
      setMessage({
        text: canShare ? 'Invito pronto per la condivisione.' : 'Link di invito copiato.',
      });
    } catch {
      setMessage({ text: 'Invito creato. La condivisione è stata annullata.' });
    }
    setSheet(null);
    return true;
  }

  async function move(item: Item, position: number) {
    if (!canEdit) return;
    const result = await call('move_setlist_unit', {
      p_setlist_id: selected,
      p_item_id: item.id,
      p_new_position: position,
    });
    if (!result) return;
    setMoveItem(null);
    setMessage({ text: item.block_id ? 'Blocco spostato.' : 'Brano spostato.' });
  }

  async function removeMember(profileId: string) {
    const result = await call('remove_setlist_member', {
      p_setlist_id: selected,
      p_profile_id: profileId,
    });
    if (result) setMessage({ text: 'Membro rimosso dalla scaletta.' });
  }

  async function revokeInvite(inviteId: string) {
    const result = await call('revoke_invite', { p_invite_id: inviteId });
    if (result) setMessage({ text: 'Invito revocato.' });
  }

  async function leaveCurrentSetlist() {
    confirmAction({
      title: 'Esci dalla scaletta?',
      copy: 'La scaletta resterà disponibile per gli altri partecipanti. Per rientrare dovrai ricevere un nuovo invito.',
      action: async () => {
        const result = await call('leave_setlist', { p_setlist_id: selected });
        if (!result) return;
        setSelected('');
        setSheet(null);
        await onRefresh();
        navigate('home');
      },
    });
  }

  async function deleteCurrentSetlist() {
    confirmAction({
      title: 'Cancella scaletta?',
      copy: 'La scaletta sarà eliminata definitivamente per tutti i partecipanti. Questa azione non può essere annullata.',
      danger: true,
      action: async () => {
        const result = await call('delete_setlist', { p_setlist_id: selected });
        if (!result) return;
        setSelected('');
        setSheet(null);
        await onRefresh();
        navigate('home');
      },
    });
  }

  function confirmLogout() {
    setSheet(null);
    confirmAction({
      title: 'Uscire da questo dispositivo?',
      copy: 'La sessione verrà rimossa soltanto da questo dispositivo. Le scalette e i brani condivisi resteranno salvati. Per rientrare servirà un nuovo invito personale.',
      confirmLabel: 'Esci',
      action: onLogout,
    });
  }
  async function sendSongs(recipientId: string) {
    if (checked.length === 0) {
      setMessage({ text: 'Scegli prima uno o più brani nell’Archivio.', error: true });
      return;
    }
    const result = await call('send_songs', {
      p_recipient_id: recipientId,
      p_setlist_id: selected || null,
      p_song_ids: checked,
      p_idempotency_key: crypto.randomUUID(),
    });
    if (result) {
      setRecentCollaboratorIds((current) => {
        const next = recordRecentCollaborator(current, recipientId);
        localStorage.setItem('scaletta:recent-collaborators', JSON.stringify(next));
        return next;
      });
      setChecked([]);
      setSheet(null);
      setMessage({ text: 'Brani inviati al collaboratore.' });
    }
  }

  function startLongPress(event: ReactPointerEvent, item: Item) {
    if (!canEdit) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    longPress.current?.start(event.clientX, event.clientY, () => {
      window.getSelection()?.removeAllRanges();
      if ('vibrate' in navigator && typeof navigator.vibrate === 'function')
        navigator.vibrate(40);
      setActiveItem(item);
      setSheet('actions');
      window.setTimeout(() => longPress.current?.clearTapSuppression(), 350);
    });
  }
  function moveLongPress(event: ReactPointerEvent) {
    longPress.current?.move(event.clientX, event.clientY);
  }
  function cancelLongPress() {
    longPress.current?.end();
    window.getSelection()?.removeAllRanges();
    window.setTimeout(() => window.getSelection()?.removeAllRanges(), 0);
    window.setTimeout(() => window.getSelection()?.removeAllRanges(), 120);
  }
  function startLibraryLongPress(event: ReactPointerEvent, song: Song) {
    if ((event.target as HTMLElement).closest('input')) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    longPress.current?.start(event.clientX, event.clientY, () => {
      window.getSelection()?.removeAllRanges();
      if ('vibrate' in navigator && typeof navigator.vibrate === 'function')
        navigator.vibrate(40);
      setActiveSong(song);
      setChecked((current) => (current.includes(song.id) ? current : [song.id]));
      setSheet('library-actions');
      window.setTimeout(() => longPress.current?.clearTapSuppression(), 350);
    });
  }
  function startItemPointer(event: ReactPointerEvent<HTMLElement>, item: Item) {
    if (moveItem?.id === item.id) {
      event.preventDefault();
      const target = event.currentTarget;
      target.setPointerCapture?.(event.pointerId);
      const bounds = target.getBoundingClientRect();
      dragOriginRef.current = {
        pointerY: event.clientY,
        top: bounds.top,
        height: bounds.height,
        scrollTop: songListRef.current?.scrollTop ?? 0,
      };
      dragPointerYRef.current = event.clientY;
      draggingMoveRef.current = true;
      setDraggingMove(true);
      setDragOffsetY(0);
      setDropIndex(null);
      setDropEdge(null);
      startMoveAutoScroll();
      return;
    }
    startLongPress(event, item);
  }

  function updateMovePosition(pointerY: number) {
    const origin = dragOriginRef.current;
    const list = songListRef.current;
    if (!origin || !list || !moveItem) return;
    dragPointerYRef.current = pointerY;
    const pointerOffset = pointerY - origin.pointerY;
    const scrollOffset = list.scrollTop - origin.scrollTop;
    setDragOffsetY(pointerOffset + scrollOffset);
    const movingTop = origin.top + pointerOffset;
    const movingCenter = movingTop + origin.height / 2;
    const rows = Array.from(
      list.querySelectorAll<HTMLElement>('[data-item-index]'),
    ).filter((row) => row.dataset.itemId !== moveItem.id);
    let next: number | null = null;
    let nextEdge: typeof dropEdge = null;
    for (const row of rows) {
      const bounds = row.getBoundingClientRect();
      if (movingCenter >= bounds.top && movingCenter < bounds.bottom) {
        next = Number(row.dataset.itemIndex);
        nextEdge = { itemIndex: Number(row.dataset.itemIndex), side: 'before' };
        break;
      }
    }
    const last = rows.at(-1)?.getBoundingClientRect();
    if (last && movingCenter >= last.top + last.height / 2) {
      const lastIndex = Number(rows.at(-1)?.dataset.itemIndex);
      next = state.items.length;
      nextEdge = { itemIndex: lastIndex, side: 'after' };
    }
    setDropIndex(next);
    setDropEdge(nextEdge);
  }

  function stopMoveAutoScroll() {
    if (autoScrollFrameRef.current !== null)
      window.cancelAnimationFrame(autoScrollFrameRef.current);
    autoScrollFrameRef.current = null;
    dragPointerYRef.current = null;
  }

  function startMoveAutoScroll() {
    stopMoveAutoScroll();
    const tick = () => {
      if (!draggingMoveRef.current) {
        stopMoveAutoScroll();
        return;
      }
      const list = songListRef.current;
      const pointerY = dragPointerYRef.current;
      if (list && pointerY !== null) {
        const bounds = list.getBoundingClientRect();
        const edge = 72;
        let speed = 0;
        if (pointerY < bounds.top + edge)
          speed = -Math.ceil(((bounds.top + edge - pointerY) / edge) * 14);
        else if (pointerY > bounds.bottom - edge)
          speed = Math.ceil(((pointerY - (bounds.bottom - edge)) / edge) * 14);
        if (speed !== 0) {
          const previous = list.scrollTop;
          list.scrollTop += speed;
          if (list.scrollTop !== previous) updateMovePosition(pointerY);
        }
      }
      autoScrollFrameRef.current = window.requestAnimationFrame(tick);
    };
    autoScrollFrameRef.current = window.requestAnimationFrame(tick);
  }

  function moveItemPointer(event: ReactPointerEvent<HTMLElement>) {
    if (draggingMoveRef.current && moveItem) {
      event.preventDefault();
      updateMovePosition(event.clientY);
      return;
    }
    moveLongPress(event);
  }

  async function endItemPointer(item: Item, index: number) {
    if (draggingMoveRef.current && moveItem?.id === item.id) {
      draggingMoveRef.current = false;
      stopMoveAutoScroll();
      setDraggingMove(false);
      dragOriginRef.current = null;
      setDragOffsetY(0);
      const insertion = dropIndex;
      setDropIndex(null);
      setDropEdge(null);
      const destination = insertion === null ? index : moveDestination(index, insertion);
      if (insertion !== null && destination !== index && destination >= 0)
        await move(item, destination);
      else setMoveItem(null);
      return;
    }
    cancelLongPress();
  }

  function cancelMove() {
    draggingMoveRef.current = false;
    stopMoveAutoScroll();
    setDraggingMove(false);
    setDropIndex(null);
    setDropEdge(null);
    setDragOffsetY(0);
    dragOriginRef.current = null;
    longPress.current?.end();
    setMoveItem(null);
  }

  function confirmChain(item: Item) {
    const index = state.items.findIndex((candidate) => candidate.id === item.id);
    const next = state.items[index + 1];
    if (!next) {
      setMessage({
        text: 'Non esiste un blocco successivo da concatenare.',
        error: true,
      });
      return;
    }
    confirmAction({
      title: 'Blocca questi due brani?',
      copy: `“${item.title}” e “${next.title}” verranno eseguiti e spostati insieme, mantenendo quest’ordine.`,
      confirmLabel: 'Blocca',
      action: async () => {
        const result = await call('create_setlist_chain', {
          p_setlist_id: selected,
          p_first_id: item.id,
          p_second_id: next.id,
        });
        if (!result) return;
        const blockId = item.block_id ?? `demo-chain-${item.id}`;
        setState((current) => ({
          ...current,
          items: current.items.map((candidate) =>
            candidate.id === item.id || candidate.id === next.id
              ? {
                  ...candidate,
                  block_id: blockId,
                  block_ordinal: candidate.id === item.id ? 0 : 1,
                }
              : candidate,
          ),
          structures: createChain(
            current.structures ?? emptyStructures(),
            item.id,
            next.id,
          ),
        }));
        setSheet(null);
        setMessage({ text: 'Catena creata.' });
      },
    });
  }

  function confirmBranch(item: Item) {
    const index = state.items.findIndex((candidate) => candidate.id === item.id);
    const main = state.items[index + 1];
    const secondary = state.items[index + 2];
    if (!main || !secondary) {
      setMessage({
        text: 'Servono almeno due blocchi successivi per il Bivio demo.',
        error: true,
      });
      return;
    }
    confirmAction({
      title: 'Crea Bivio?',
      copy: `Da “${item.title}” partiranno il percorso principale “${main.title}” e il nuovo ramo “${secondary.title}”.`,
      confirmLabel: 'Crea Bivio',
      action: async () => {
        const result = await call('create_setlist_branch', {
          p_setlist_id: selected,
          p_origin_id: item.id,
          p_main_id: main.id,
          p_branch_id: secondary.id,
        });
        if (!result) return;
        setState((current) => ({
          ...current,
          structures: {
            ...(current.structures ?? emptyStructures()),
            branches: [
              ...(current.structures?.branches ?? []).filter(
                (branch) => branch.originId !== item.id,
              ),
              {
                id: `branch-${item.id}`,
                originId: item.id,
                mainPathIds: [main.id],
                branchPathIds: [secondary.id],
              },
            ],
          },
        }));
        setSheet(null);
        setMessage({ text: 'Bivio creato.' });
      },
    });
  }

  function confirmAlternative(item: Item) {
    const index = state.items.findIndex((candidate) => candidate.id === item.id);
    const alternative = state.items[index + 1];
    if (!alternative) {
      setMessage({
        text: 'Non esiste un blocco successivo da impostare come Alternativa.',
        error: true,
      });
      return;
    }
    confirmAction({
      title: 'Crea Alternativa?',
      copy: `“${item.title}” e “${alternative.title}” occuperanno la stessa posizione logica e avranno la stessa prosecuzione.`,
      confirmLabel: 'Crea Alternativa',
      action: async () => {
        const result = await call('create_setlist_alternative', {
          p_setlist_id: selected,
          p_origin_id: item.id,
          p_alternative_id: alternative.id,
        });
        if (!result) return;
        setState((current) => ({
          ...current,
          structures: createAlternative(
            current.structures ?? emptyStructures(),
            item.id,
            alternative.id,
          ),
        }));
        setSheet(null);
        setMessage({ text: 'Alternativa creata.' });
      },
    });
  }

  function confirmRemoveChain(item: Item) {
    const relation = chainForItem(structures, item.id);
    if (!relation) return;
    confirmAction({
      title: 'Togli il blocco?',
      copy: 'I due brani resteranno nella scaletta e potranno essere spostati separatamente.',
      confirmLabel: 'Togli blocco',
      action: async () => {
        const result = await call('remove_setlist_chain', {
          p_setlist_id: selected,
          p_item_id: item.id,
        });
        if (!result) return;
        setState((current) => ({
          ...current,
          items: current.items.map((candidate) =>
            relation.itemIds.includes(candidate.id)
              ? { ...candidate, block_id: null, block_ordinal: null }
              : candidate,
          ),
          structures: removeChain(current.structures ?? emptyStructures(), item.id),
        }));
        setSheet(null);
        setMessage({ text: 'Blocco sciolto.' });
      },
    });
  }

  async function toggleBlockProposal(item: Item) {
    const nextValue = !item.is_proposal;
    const result = await call('set_block_proposal', {
      p_setlist_id: selected,
      p_item_id: item.id,
      p_is_proposal: nextValue,
    });
    if (!result) return;
    setState((current) => ({
      ...current,
      items: current.items.map((candidate) =>
        item.block_id
          ? candidate.block_id === item.block_id
            ? { ...candidate, is_proposal: nextValue }
            : candidate
          : candidate.id === item.id
            ? { ...candidate, is_proposal: nextValue }
            : candidate,
      ),
    }));
    setSheet(null);
    setMessage({
      text: nextValue
        ? 'Blocco contrassegnato come proposta.'
        : 'Proposta approvata e resa definitiva.',
    });
  }

  function confirmRemoveBranch(item: Item) {
    if (!branchForItem(structures, item.id)) return;
    confirmAction({
      title: 'Togli il Bivio?',
      copy: 'I due percorsi torneranno nella sequenza verticale della scaletta.',
      confirmLabel: 'Togli Bivio',
      action: async () => {
        const result = await call('remove_setlist_branch', {
          p_setlist_id: selected,
          p_item_id: item.id,
        });
        if (!result) return;
        setState((current) => ({
          ...current,
          structures: removeBranch(current.structures ?? emptyStructures(), item.id),
        }));
        setSheet(null);
        setMessage({ text: 'Bivio rimosso.' });
      },
    });
  }

  function confirmRemoveAlternative(item: Item) {
    if (!alternativeForItem(structures, item.id)) return;
    confirmAction({
      title: "Togli l'alternativa?",
      copy: 'I due blocchi torneranno nella sequenza verticale della scaletta.',
      confirmLabel: 'Togli alternativa',
      action: async () => {
        const result = await call('remove_setlist_alternative', {
          p_setlist_id: selected,
          p_item_id: item.id,
        });
        if (!result) return;
        setState((current) => ({
          ...current,
          structures: removeAlternative(current.structures ?? emptyStructures(), item.id),
        }));
        setSheet(null);
        setMessage({ text: 'Alternativa rimossa.' });
      },
    });
  }

  const common = { onHome: () => navigate('home'), onMenu: () => setSheet('menu') };
  return (
    <main className="stage">
      {demoMode && view !== 'home' && <div className="demo-badge">Collaudo locale</div>}
      <section className={`phone ${view}-screen`}>
        {view === 'home' && (
          <Home
            setlists={setlists}
            pending={unreadItems}
            proposalPending={unreadProposals}
            proposalCounts={proposalCounts}
            libraryCount={state.songs.length}
            onOpen={openSetlist}
            onNavigate={(next) => {
              navigate(next);
            }}
            onAddSong={() => {
              setActiveSong(null);
              setSongNeedsDestination(true);
              applyUiAction({ type: 'open-panel', panel: 'song-form' });
            }}
            {...common}
          />
        )}
        {view === 'setlists' && (
          <>
            <ScreenHeader eyebrow="Jammo" title="Le mie scalette" {...common} />
            <div className="library-info">
              <span>{setlists.length} disponibili</span>
              <button
                type="button"
                onClick={() => setSheet('create-setlist')}
                aria-label="Crea nuova scaletta"
              >
                ＋
              </button>
            </div>
            {setlists.length === 0 ? (
              <EmptyState
                title="Nessuna scaletta"
                action={
                  <button
                    type="button"
                    className="primary"
                    onClick={() => setSheet('create-setlist')}
                  >
                    ＋ Crea la prima scaletta
                  </button>
                }
              >
                Crea uno spazio per la prossima serata.
              </EmptyState>
            ) : (
              <div className="setlists-list">
                {setlists.map((entry) => (
                  <SwipeSetlistCard
                    key={entry.id}
                    entry={entry}
                    notificationCount={proposalCounts[entry.id] ?? 0}
                    onOpen={() => openSetlist(entry.id)}
                    onDelete={() => {
                      confirmAction({
                        title: `Eliminare “${entry.title}”?`,
                        copy: 'La scaletta sarà eliminata definitivamente per tutti i membri.',
                        danger: true,
                        confirmLabel: 'Elimina',
                        cancelLabel: 'Annulla',
                        action: async () => {
                          const result = await call('delete_setlist', {
                            p_setlist_id: entry.id,
                          });
                          if (!result) return;
                          if (selected === entry.id) setSelected('');
                          await onRefresh();
                          setMessage({ text: `“${entry.title}” eliminata.` });
                        },
                      });
                    }}
                  />
                ))}
              </div>
            )}
          </>
        )}
        {view === 'setlist' && (
          <>
            <ScreenHeader
              eyebrow={
                currentSetlist
                  ? currentSetlist.group_name.trim() || 'Nome gruppo'
                  : 'Le tue serate'
              }
              title={currentSetlist?.title ?? 'Le mie scalette'}
              {...common}
            />
            {!currentSetlist ? (
              <EmptyState
                title="Nessuna scaletta"
                action={
                  <button
                    type="button"
                    className="primary"
                    onClick={() => setSheet('create-setlist')}
                  >
                    ＋ Crea la prima scaletta
                  </button>
                }
              >
                Crea uno spazio per la prossima serata.
              </EmptyState>
            ) : (
              <>
                <div className={`list-info ${moveItem ? 'move-mode' : ''}`}>
                  <span>{state.items.length} brani</span>
                  {moveItem ? (
                    <small>{`Trascina “${moveItem.title}” nella nuova posizione`}</small>
                  ) : (
                    <button
                      type="button"
                      className={`notes-visibility-toggle ${notesVisible ? 'active' : ''}`}
                      role="switch"
                      aria-checked={notesVisible}
                      onClick={toggleNotesVisibility}
                    >
                      <span>Note</span>
                      <i aria-hidden="true" />
                    </button>
                  )}
                  {moveItem && (
                    <button type="button" onClick={cancelMove}>
                      Annulla
                    </button>
                  )}
                </div>
                {state.items.length === 0 ? (
                  <EmptyState
                    title="Scaletta vuota"
                    action={
                      canEdit ? (
                        <button
                          type="button"
                          className="primary"
                          onClick={openSetlistAdd}
                        >
                          ＋ Aggiungi box
                        </button>
                      ) : undefined
                    }
                  >
                    I brani compariranno qui, nell’ordine dello spettacolo.
                  </EmptyState>
                ) : (
                  <div className="song-list" ref={songListRef}>
                    {state.items.map((item, index) => {
                      if (
                        compactBranchIds.has(item.id) ||
                        compactAlternativeIds.has(item.id)
                      )
                        return null;
                      const branch = branchFromOrigin(structures, item.id);
                      const alternative = structures.alternatives.find(
                        (relation) => relation.originId === item.id,
                      );
                      const alternativeItem = alternative
                        ? state.items.find(
                            (candidate) => candidate.id === alternative.alternativeId,
                          )
                        : null;
                      const mainBranchItem = branch
                        ? state.items.find(
                            (candidate) => candidate.id === branch.mainPathIds[0],
                          )
                        : null;
                      const secondaryBranchItem = branch
                        ? state.items.find(
                            (candidate) => candidate.id === branch.branchPathIds[0],
                          )
                        : null;
                      return (
                        <Fragment key={item.id}>
                          <SetlistItem
                            key={item.id}
                            item={item}
                            index={index}
                            showNotes={notesVisible}
                            highlighted={highlightedItem === item.id}
                            contextActive={
                              activeItem?.id === item.id && sheet === 'actions'
                            }
                            moveReady={moveItem?.id === item.id}
                            dragging={draggingMove && moveItem?.id === item.id}
                            dragOffsetY={
                              draggingMove && moveItem?.id === item.id ? dragOffsetY : 0
                            }
                            dropBefore={
                              draggingMove &&
                              dropEdge?.itemIndex === index &&
                              dropEdge.side === 'before'
                            }
                            dropAfter={
                              draggingMove &&
                              dropEdge?.itemIndex === index &&
                              dropEdge.side === 'after'
                            }
                            onPointerDown={(event) => startItemPointer(event, item)}
                            onPointerMove={moveItemPointer}
                            onPointerEnd={() => void endItemPointer(item, index)}
                            onPointerCancel={cancelMove}
                          />
                          {chainBetween(
                            structures,
                            item.id,
                            state.items[index + 1]?.id,
                          ) && (
                            <div
                              className="chain-connector"
                              role="img"
                              aria-label="Catena fra i due blocchi"
                            >
                              <img src="/icons/catena.svg" alt="" />
                            </div>
                          )}
                          {branch && mainBranchItem && secondaryBranchItem && (
                            <BranchPair
                              main={mainBranchItem}
                              secondary={secondaryBranchItem}
                              onPointerDown={(event, selectedItem) =>
                                startLongPress(event, selectedItem)
                              }
                              onPointerMove={moveLongPress}
                              onPointerEnd={cancelLongPress}
                            />
                          )}
                          {alternativeItem && (
                            <AlternativePair
                              main={item}
                              alternative={alternativeItem}
                              onPointerDown={(event, selectedItem) =>
                                startLongPress(event, selectedItem)
                              }
                              onPointerMove={moveLongPress}
                              onPointerEnd={cancelLongPress}
                            />
                          )}
                        </Fragment>
                      );
                    })}
                  </div>
                )}
                {canEdit && (
                  <button
                    type="button"
                    className="add-song-button"
                    onClick={openSetlistAdd}
                  >
                    ＋ Aggiungi box
                  </button>
                )}
              </>
            )}
          </>
        )}
        {view === 'library' && (
          <>
            <ScreenHeader
              eyebrow="Archivio personale"
              title="Archivio brani"
              {...common}
            />
            {libraryReturnSetlistId && (
              <button
                type="button"
                className="library-return-button"
                onClick={returnFromLibraryToSetlist}
              >
                <span aria-hidden="true">←</span>
                Torna a{' '}
                {setlists.find((entry) => entry.id === libraryReturnSetlistId)?.title ??
                  'scaletta'}
              </button>
            )}
            <div className="library-info">
              <span>{state.songs.length} brani</span>
              <button
                type="button"
                onClick={() => {
                  setActiveSong(null);
                  setSongNeedsDestination(false);
                  setSheet('song-form');
                }}
                aria-label="Aggiungi un brano"
              >
                ＋
              </button>
            </div>
            {state.songs.length > 0 && (
              <label className="library-search">
                <span className="sr-only">Cerca per titolo o autore</span>
                <input
                  type="search"
                  value={libraryQuery}
                  onChange={(event) => setLibraryQuery(event.target.value)}
                  placeholder="Cerca per titolo o autore"
                />
              </label>
            )}
            {state.songs.length === 0 ? (
              <EmptyState
                title="Archivio vuoto"
                action={
                  <button
                    type="button"
                    className="primary"
                    onClick={() => setSheet('song-form')}
                  >
                    Aggiungi il primo brano
                  </button>
                }
              >
                Salva qui i brani che usi nelle tue serate.
              </EmptyState>
            ) : (
              <div className="library-list">
                {filterSongs(state.songs, libraryQuery).map((song) => (
                  <LibraryCard
                    key={song.id}
                    song={song}
                    selected={checked.includes(song.id)}
                    highlighted={highlightedSong === song.id}
                    onSelect={() =>
                      setChecked((value) =>
                        value.includes(song.id)
                          ? value.filter((id) => id !== song.id)
                          : [...value, song.id],
                      )
                    }
                    onPointerDown={(event) => startLibraryLongPress(event, song)}
                    onPointerMove={moveLongPress}
                    onPointerEnd={cancelLongPress}
                  />
                ))}
                {filterSongs(state.songs, libraryQuery).length === 0 && (
                  <p className="library-no-results">Nessun brano trovato.</p>
                )}
              </div>
            )}
            {checked.length > 0 && (
              <section
                className="library-selection-bar"
                aria-label="Azioni brani selezionati"
              >
                <span>
                  <b>{checked.length}</b>{' '}
                  {checked.length === 1 ? 'brano selezionato' : 'brani selezionati'}
                </span>
                <button
                  type="button"
                  className="primary"
                  disabled={setlists.length === 0}
                  onClick={() => {
                    const first = state.songs.find((song) => checked.includes(song.id));
                    if (!first) return;
                    setActiveSong(first);
                    setSheet('song-destination');
                  }}
                >
                  Aggiungi alla scaletta
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const first = state.songs.find((song) => checked.includes(song.id));
                    if (!first) return;
                    setActiveSong(first);
                    setSheet('library-actions');
                  }}
                >
                  Altre azioni
                </button>
              </section>
            )}
          </>
        )}
        {view === 'inbox' && (
          <>
            <ScreenHeader eyebrow="Inbox" title="Nuovi arrivi" {...common} />
            <div className="inbox-summary">
              <span>
                <b>{pending.reduce((sum, transfer) => sum + transfer.items.length, 0)}</b>{' '}
                brani nuovi
              </span>
              <span>
                <b>{pending.length}</b> invii
              </span>
            </div>
            <section className="inbox-filters" aria-label="Filtri Inbox">
              <label>
                Ordina
                <select
                  value={inboxSort}
                  onChange={(event) => setInboxSort(event.target.value as InboxSort)}
                >
                  <option value="newest">Più recenti</option>
                  <option value="oldest">Meno recenti</option>
                </select>
              </label>
              <label>
                Inviato da
                <select
                  value={inboxSenderId}
                  onChange={(event) => setInboxSenderId(event.target.value)}
                >
                  <option value="">Tutti</option>
                  <option value="self">Importato da me</option>
                  {state.collaborators.map((person) => (
                    <option key={person.profile_id} value={person.profile_id}>
                      {person.display_name} · {person.instrument_role}
                    </option>
                  ))}
                </select>
              </label>
              <button
                type="button"
                onClick={() => {
                  setInboxSenderId('');
                  setInboxSort('newest');
                  setInboxCollaboratorQuery('');
                }}
              >
                Azzera filtri
              </button>
              <div className="inbox-collaborators">
                <strong>Collaboratori</strong>
                <input
                  type="search"
                  placeholder="Cerca nome, strumento o telefono"
                  value={inboxCollaboratorQuery}
                  onChange={(event) => setInboxCollaboratorQuery(event.target.value)}
                />
                <button
                  type="button"
                  className={inboxSenderId === 'self' ? 'selected' : ''}
                  onClick={() => setInboxSenderId('self')}
                >
                  Importato da me
                </button>
                {inboxCollaborators.map((person) => (
                  <button
                    type="button"
                    className={inboxSenderId === person.profile_id ? 'selected' : ''}
                    key={person.profile_id}
                    onClick={() => setInboxSenderId(person.profile_id)}
                  >
                    <span>
                      {person.display_name} · {person.instrument_role}
                    </span>
                    <small>{formatItalianPhone(person.phone)}</small>
                  </button>
                ))}
              </div>
            </section>
            {pending.length === 0 ? (
              <EmptyState icon="↙" title="Tutto organizzato">
                Non ci sono brani in attesa.
              </EmptyState>
            ) : (
              <div className="inbox-history">
                {inboxGroups.map((group) => (
                  <section key={group.day}>
                    <h2>
                      {group.day === 'legacy'
                        ? 'Data non disponibile'
                        : new Date(`${group.day}T12:00:00`).toLocaleDateString('it-IT', {
                            weekday: 'long',
                            day: 'numeric',
                            month: 'long',
                          })}
                    </h2>
                    <div className="inbox-list">
                      {group.transfers.map((transfer) => (
                        <article
                          className={`inbox-card ${readTransferIds.has(transfer.id) ? 'read' : 'unread'}`}
                          key={transfer.id}
                        >
                          <button
                            type="button"
                            className="inbox-open"
                            onClick={() => markTransferRead(transfer.id)}
                          >
                            <span className="inbox-type">♪</span>
                            <span className="inbox-copy">
                              <small>
                                {transfer.items.length > 1
                                  ? `${transfer.items.length} brani`
                                  : 'Brano ricevuto'}
                              </small>
                              <span className="inbox-meta">
                                {transfer.created_at
                                  ? new Date(transfer.created_at).toLocaleString(
                                      'it-IT',
                                      {
                                        day: '2-digit',
                                        month: '2-digit',
                                        year: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit',
                                      },
                                    )
                                  : 'Data e ora non disponibili'}
                                {' · '}
                                {transfer.origin === 'self_import'
                                  ? 'Importato da me'
                                  : transfer.sender_instrument || transfer.sender_phone
                                    ? `${transfer.sender_instrument ?? 'Collaboratore'}${transfer.sender_phone ? ` · ${formatItalianPhone(transfer.sender_phone)}` : ''}`
                                    : 'Provenienza non disponibile'}
                              </span>
                              <strong>{transfer.items[0]?.title}</strong>
                              {transfer.items.slice(1).map((item) => (
                                <span key={item.ordinal}>{item.title}</span>
                              ))}
                            </span>
                          </button>
                          <div className="inbox-actions">
                            <button
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();
                                markTransferRead(transfer.id);
                                call('respond_transfer', {
                                  p_transfer_id: transfer.id,
                                  p_accept: false,
                                });
                              }}
                            >
                              Rifiuta
                            </button>
                            <button
                              type="button"
                              className="primary"
                              onClick={(event) => {
                                event.stopPropagation();
                                markTransferRead(transfer.id);
                                call('respond_transfer', {
                                  p_transfer_id: transfer.id,
                                  p_accept: true,
                                });
                              }}
                            >
                              Archivio
                            </button>
                          </div>
                        </article>
                      ))}
                    </div>
                  </section>
                ))}
                {inboxGroups.length === 0 && (
                  <EmptyState icon="⌕" title="Nessun risultato">
                    Modifica o azzera i filtri.
                  </EmptyState>
                )}
              </div>
            )}
          </>
        )}
        {view === 'map' && (
          <>
            <ScreenHeader
              eyebrow={
                currentSetlist
                  ? currentSetlist.group_name.trim() || 'Nome gruppo'
                  : 'Vista mappa'
              }
              title={currentSetlist?.title ?? 'Mappa'}
              {...common}
            />
            <SetlistMap items={state.items} structures={structures} />
          </>
        )}
        {view !== 'home' && (
          <BottomNav
            current={view}
            onChange={(next) => {
              if (next === 'map') {
                if (view === 'map') navigate('setlist');
                else if (view === 'setlist' && currentSetlist) navigate('map');
                return;
              }
              navigate(next);
            }}
            canOpenMap={view === 'setlist' && !!currentSetlist}
          />
        )}
      </section>
      {sheet === 'menu' && (
        <Menu
          onClose={() => {
            setSheet(null);
            setActiveItem(null);
            cancelMove();
          }}
          onNavigate={(next) => {
            navigate(next);
          }}
          onOpen={(name) => name && applyUiAction({ type: 'open-panel', panel: name })}
          canManageSetlist={view === 'setlist' && !!currentSetlist}
          onPrint={() => {
            setSheet(null);
            window.setTimeout(() => window.print(), 120);
          }}
          onLeave={leaveCurrentSetlist}
          onDelete={deleteCurrentSetlist}
          onLogout={confirmLogout}
        />
      )}
      {sheet === 'rename-setlist' && currentSetlist && (
        <RenameSetlist
          currentTitle={currentSetlist.title}
          onClose={() => setSheet(null)}
          onRename={async (title) => {
            const result = await call('rename_setlist', {
              p_setlist_id: currentSetlist.id,
              p_title: title,
            });
            if (!result) return false;
            await onRefresh();
            await load();
            setSheet(null);
            setMessage({ text: 'Nome della scaletta aggiornato per tutti.' });
            return true;
          }}
        />
      )}
      {sheet === 'rename-group' && currentSetlist && (
        <RenameSharedName
          title="Rinomina gruppo"
          label="Nome gruppo"
          currentValue={currentSetlist.group_name}
          onClose={() => setSheet(null)}
          onRename={async (groupName) => {
            const result = await call('rename_setlist_group', {
              p_setlist_id: currentSetlist.id,
              p_group_name: groupName,
            });
            if (!result) return false;
            await onRefresh();
            await load();
            setSheet(null);
            setMessage({ text: 'Nome del gruppo aggiornato per tutti.' });
            return true;
          }}
        />
      )}

      {sheet === 'create-setlist' && (
        <CreateSetlist
          onClose={() => setSheet(null)}
          onCreate={async (groupName, title) =>
            (await call('create_setlist', {
              p_group_name: groupName,
              p_title: title,
            })) !== null
          }
          onDone={async () => {
            setSheet(null);
            await onRefresh();
            await load();
            setMessage({ text: 'Scaletta creata.' });
          }}
        />
      )}
      {sheet === 'song-form' && (
        <SongForm
          song={activeSong}
          requireDestination={songNeedsDestination && !activeSong}
          setlists={setlists}
          onClose={() => setSheet(null)}
          onSave={async (id, title, author, url, destination) => {
            const result = await call('save_song', {
              p_id: id,
              p_title: title,
              p_author: author,
              p_source_url: url,
            });
            if (result) {
              const songId = typeof result.data === 'string' ? result.data : id;
              if (destination?.type === 'setlist' && songId) {
                const added = await call('add_song_to_setlist', {
                  p_setlist_id: destination.setlistId,
                  p_song_id: songId,
                });
                if (!added) return;
                setSelected(destination.setlistId);
                navigate('setlist');
                if (typeof added.data === 'string') reveal(added.data, 'item');
              } else if (!id && songId) {
                navigate('library');
                reveal(songId, 'song');
              }
              setSheet(null);
              setMessage({ text: id ? 'Brano aggiornato.' : 'Brano aggiunto.' });
            }
          }}
        />
      )}
      {sheet === 'library-actions' && activeSong && (
        <LibraryActionSheet
          song={activeSong}
          canAdd={setlists.length > 0}
          onClose={() => {
            setSheet(null);
            setActiveSong(null);
            setChecked([]);
          }}
          onSend={() => setSheet('collaborators')}
          onEdit={() => {
            setSongNeedsDestination(false);
            setSheet('song-form');
          }}
          onAdd={() => setSheet('song-destination')}
          onDelete={() => {
            confirmAction({
              title: 'Eliminare il brano dall’Archivio?',
              copy: 'Il brano verrà eliminato dall’Archivio personale, ma resterà nelle scalette in cui è già stato inserito.',
              danger: true,
              confirmLabel: 'Elimina brano',
              action: async () => {
                if (!demoMode) {
                  setMessage({
                    text: 'La cancellazione richiede una migration dedicata, non ancora applicata al database remoto.',
                    error: true,
                  });
                  return;
                }
                const result = await call('delete_song', { p_song_id: activeSong.id });
                if (!result) return;
                setChecked([]);
                setActiveSong(null);
                setSheet(null);
                setMessage({ text: 'Brano eliminato dall’Archivio.' });
              },
            });
          }}
        />
      )}
      {sheet === 'song-destination' && activeSong && (
        <SongDestinationSheet
          setlists={setlists}
          onClose={() => setSheet('library-actions')}
          onChoose={async (setlistId) => {
            const songIds = checked.length > 0 ? [...checked] : [activeSong.id];
            const outcome = await runSequentially(songIds, async (songId) => {
              const result = await call('add_song_to_setlist', {
                p_setlist_id: setlistId,
                p_song_id: songId,
              });
              return result
                ? { ok: true as const }
                : {
                    ok: false as const,
                    error: lastCallError.current || 'Aggiunta non completata.',
                  };
            });
            setChecked(outcome.pendingIds);
            if (outcome.error) return;
            setChecked([]);
            setActiveSong(null);
            setSheet(null);
            setMessage({
              text:
                songIds.length === 1
                  ? 'Brano aggiunto alla scaletta.'
                  : `${songIds.length} brani aggiunti alla scaletta.`,
            });
          }}
        />
      )}
      {sheet === 'item-form' && activeItem && (
        <SetlistItemForm
          item={activeItem}
          onClose={() => setSheet(null)}
          onSave={async (title, author, url, notes) => {
            const result = await call('update_setlist_item', {
              p_setlist_id: selected,
              p_item_id: activeItem.id,
              p_title: title,
              p_author: author,
              p_source_url: url,
              p_notes: notes,
            });
            if (!result) return;
            setSheet(null);
            setMessage({
              text: activeItem.block_id ? 'Blocco aggiornato.' : 'Brano aggiornato.',
            });
          }}
        />
      )}
      {sheet === 'actions' && activeItem && (
        <ActionSheet
          item={activeItem}
          ordinal={Math.max(
            1,
            state.items.findIndex((item) => item.id === activeItem.id) + 1,
          )}
          onClose={() => {
            setSheet(null);
            setActiveItem(null);
            cancelMove();
          }}
          onMove={() => {
            setMoveItem(activeItem);
            setSheet(null);
          }}
          onEdit={() => setSheet('item-form')}
          onInsert={() => setSheet('insert-box')}
          canLock={
            !!chainForItem(structures, activeItem.id) ||
            state.items.findIndex((item) => item.id === activeItem.id) <
              state.items.length - 1
          }
          chainActive={!!chainForItem(structures, activeItem.id)}
          branchActive={!!branchForItem(structures, activeItem.id)}
          alternativeActive={!!alternativeForItem(structures, activeItem.id)}
          proposalActive={!!activeItem.is_proposal}
          onLock={() =>
            chainForItem(structures, activeItem.id)
              ? confirmRemoveChain(activeItem)
              : confirmChain(activeItem)
          }
          onBranch={() =>
            branchForItem(structures, activeItem.id)
              ? confirmRemoveBranch(activeItem)
              : confirmBranch(activeItem)
          }
          onAlternative={() =>
            alternativeForItem(structures, activeItem.id)
              ? confirmRemoveAlternative(activeItem)
              : confirmAlternative(activeItem)
          }
          onProposal={() => void toggleBlockProposal(activeItem)}
          onRemove={async () => {
            confirmAction({
              title: 'Cancella blocco dalla scaletta?',
              copy: 'Il blocco verrà rimosso soltanto da questa scaletta. Il brano resterà disponibile nel tuo Archivio personale.',
              danger: true,
              confirmLabel: 'Cancella blocco',
              action: async () => {
                const result = await call('remove_setlist_unit', {
                  p_setlist_id: selected,
                  p_item_id: activeItem.id,
                });
                if (!result) return;
                setActiveItem(null);
                setSheet(null);
                setMessage({
                  text: activeItem.block_id ? 'Blocco eliminato.' : 'Brano eliminato.',
                });
              },
            });
          }}
        />
      )}
      {sheet === 'insert-box' && (
        <InsertBoxSheet
          songs={state.songs}
          onClose={() => setSheet(null)}
          onEmpty={async (text, notes) => {
            const result = await call('insert_empty_setlist_box', {
              p_setlist_id: selected,
              p_after_item_id: activeItem?.id ?? null,
              p_text: text,
              p_notes: notes,
            });
            if (!result) return false;
            setSheet(null);
            setMessage({ text: 'Box vuoto inserito.' });
            return true;
          }}
          onSong={async (songId) => {
            const sourceSong = state.songs.find((song) => song.id === songId);
            if (
              !sourceSong ||
              (activeItem && !state.items.some((item) => item.id === activeItem.id))
            ) {
              setMessage({
                text: 'Brano o posizione di inserimento non disponibili.',
                error: true,
              });
              return false;
            }
            const inserted = await call('insert_setlist_song_box', {
              p_setlist_id: selected,
              p_after_item_id: activeItem?.id ?? null,
              p_song_id: songId,
            });
            if (!inserted) return false;
            setSheet(null);
            setActiveItem(null);
            setMessage({ text: 'Brano inserito.' });
            reveal(String(inserted.data), 'item');
            return true;
          }}
        />
      )}
      {sheet === 'invite' && (
        <InviteSheet
          setlistTitle={currentSetlist?.title ?? ''}
          onClose={() => setSheet(null)}
          onInvite={invite}
          pendingInvites={state.pending_invites}
          onRevoke={revokeInvite}
        />
      )}
      {sheet === 'collaborators' && (
        <CollaboratorsSheet
          collaborators={state.collaborators}
          recentIds={recentCollaboratorIds}
          selectedCount={checked.length}
          onSend={sendSongs}
          onRemove={removeMember}
          onClose={() => setSheet(null)}
        />
      )}
      {sheet === 'profile' && (
        <ProfileSheet
          profile={profile}
          onClose={() => setSheet(null)}
          onSave={async (displayName, instrument) => {
            const result = await call('update_my_profile', {
              p_display_name: displayName,
              p_instrument: instrument,
            });
            if (!result) return;
            await onRefresh();
            setSheet(null);
            setMessage({ text: 'Profilo aggiornato.' });
          }}
        />
      )}
      {sheet === 'guide' && <GuideSheet onClose={() => setSheet(null)} />}
      {confirmation && (
        <ConfirmDialog
          {...confirmation}
          onCancel={() => setConfirmation(null)}
          onConfirm={async () => {
            const action = confirmation.action;
            setConfirmation(null);
            await action();
          }}
        />
      )}
      {message && <Toast error={message.error}>{message.text}</Toast>}
    </main>
  );
}

function Home({
  setlists,
  pending,
  proposalPending,
  proposalCounts,
  libraryCount,
  onOpen,
  onNavigate,
  onAddSong,
  onHome,
  onMenu,
}: {
  setlists: Setlist[];
  pending: number;
  proposalPending: number;
  proposalCounts: Record<string, number>;
  libraryCount: number;
  onOpen: (id: string) => void;
  onNavigate: (view: AppView) => void;
  onAddSong: () => void;
  onHome: () => void;
  onMenu: () => void;
}) {
  return (
    <>
      <ScreenHeader
        home
        eyebrow="Organizza i brani"
        title="Jammo"
        onHome={onHome}
        onMenu={onMenu}
      />
      <div className="home-choices">
        <button
          type="button"
          className="home-choice active"
          onClick={() => onNavigate('setlists')}
        >
          <span className="choice-icon">
            ≡{proposalPending > 0 && <b>{proposalPending}</b>}
          </span>
          <span className="home-quantity">{setlists.length}</span>
          <strong>Le mie scalette</strong>
          <small>Collegate al tuo profilo</small>
        </button>
        <button type="button" className="home-choice" onClick={() => onNavigate('inbox')}>
          <span className="choice-icon">↙{pending > 0 && <b>{pending}</b>}</span>
          <strong>Inbox</strong>
          <small>{pending ? `${pending} nuovi` : 'Nessun nuovo arrivo'}</small>
        </button>
        <button type="button" className="home-choice" onClick={onAddSong}>
          <span>＋</span>
          <strong>Aggiungi brano</strong>
          <small>Link, titolo, autore e note</small>
        </button>
        <button
          type="button"
          className="home-choice"
          onClick={() => onNavigate('library')}
        >
          <span className="choice-icon">♫</span>
          <span className="home-quantity">{libraryCount}</span>
          <strong>Archivio brani</strong>
          <small>{libraryCount === 1 ? '1 brano' : `${libraryCount} brani`}</small>
        </button>
      </div>
      <section className="recent-section">
        <div className="section-label">
          <div>
            <span>Scalette recenti</span>
            <strong>Le ultime {Math.min(2, setlists.length)} utilizzate</strong>
          </div>
        </div>
        {setlists.slice(0, 2).map((entry, index) => (
          <button
            type="button"
            className="setlist-card"
            key={entry.id}
            onClick={() => onOpen(entry.id)}
          >
            <span className="setlist-lines">
              <i />
              <i />
              <i />
            </span>
            <span>
              <strong>{entry.title}</strong>
              <small>
                {entry.group_name} · {index === 0 ? '6 brani' : '3 brani'}
              </small>
            </span>
            <em className="approved">Modifica</em>
            {(proposalCounts[entry.id] ?? 0) > 0 && (
              <span className="setlist-notification">{proposalCounts[entry.id]}</span>
            )}
            <b>›</b>
          </button>
        ))}
        {setlists.length === 0 && (
          <p className="compact-empty">Non hai ancora scalette.</p>
        )}
      </section>
    </>
  );
}

function SwipeSetlistCard({
  entry,
  notificationCount,
  onOpen,
  onDelete,
}: {
  entry: Setlist;
  notificationCount: number;
  onOpen: () => void;
  onDelete: () => void;
}) {
  const [swipe, setSwipe] = useState(0);
  const start = useRef<{ x: number; y: number } | null>(null);
  const swiping = useRef(false);
  return (
    <div className="setlist-swipe-row">
      <button
        type="button"
        className="setlist-delete-reveal"
        aria-label={`Elimina ${entry.title}`}
        onClick={onDelete}
      >
        <VisualIcon name="trash" />
        <span>Elimina</span>
      </button>
      <button
        type="button"
        className="setlist-card"
        style={{ transform: `translateX(${swipe}px)` }}
        onClick={() => {
          if (!swiping.current && swipe === 0) onOpen();
        }}
        onPointerDown={(event) => {
          start.current = { x: event.clientX, y: event.clientY };
          swiping.current = false;
          event.currentTarget.setPointerCapture?.(event.pointerId);
        }}
        onPointerMove={(event) => {
          if (!start.current) return;
          const dx = event.clientX - start.current.x;
          const dy = event.clientY - start.current.y;
          if (dx > 8 && Math.abs(dx) > Math.abs(dy) * 1.2) {
            swiping.current = true;
            setSwipe(Math.min(92, dx));
          }
        }}
        onPointerUp={() => {
          setSwipe((value) => (value >= 58 ? 92 : 0));
          start.current = null;
          window.setTimeout(() => {
            swiping.current = false;
          }, 0);
        }}
        onPointerCancel={() => {
          setSwipe(0);
          start.current = null;
          swiping.current = false;
        }}
        onContextMenu={(event) => event.preventDefault()}
      >
        <span className="setlist-lines">
          <i />
          <i />
          <i />
        </span>
        <span>
          <strong>{entry.title}</strong>
          <small>{entry.group_name} · Membro</small>
        </span>
        <em className="approved">Modifica</em>
        {notificationCount > 0 && (
          <span className="setlist-notification">{notificationCount}</span>
        )}
        <b>›</b>
      </button>
    </div>
  );
}
function SetlistItem({
  item,
  index,
  showNotes,
  highlighted,
  contextActive,
  moveReady,
  dragging,
  dragOffsetY,
  dropBefore,
  dropAfter,
  onPointerDown,
  onPointerMove,
  onPointerEnd,
  onPointerCancel,
}: {
  item: Item;
  index: number;
  showNotes: boolean;
  highlighted: boolean;
  contextActive: boolean;
  moveReady: boolean;
  dragging: boolean;
  dragOffsetY: number;
  dropBefore: boolean;
  dropAfter: boolean;
  onPointerDown: (event: ReactPointerEvent<HTMLElement>) => void;
  onPointerMove: (event: ReactPointerEvent<HTMLElement>) => void;
  onPointerEnd: () => void;
  onPointerCancel: () => void;
}) {
  return (
    <div
      data-item-id={item.id}
      data-item-index={index}
      className={`song-row ${item.block_id ? 'in-block' : ''} ${item.is_proposal ? 'is-proposal' : ''} ${highlighted ? 'just-added' : ''} ${dropBefore ? 'drop-before' : ''} ${dropAfter ? 'drop-after' : ''}`}
    >
      <span className="song-number">{index + 1}</span>
      <span className="group-rail" />
      <article
        className={`song-card ${contextActive ? 'context-active' : ''} ${moveReady ? 'move-ready' : ''} ${dragging ? 'dragging' : ''}`}
        style={{ transform: `translateY(${dragOffsetY}px)` }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerEnd}
        onPointerCancel={onPointerCancel}
        onPointerLeave={() => !moveReady && !dragging && onPointerEnd()}
        onContextMenu={(event) => event.preventDefault()}
      >
        <span className="song-text">
          {item.is_proposal && <span className="proposal-badge">Proposta</span>}
          <strong>{item.title}</strong>
          <small>
            {item.item_kind === 'text'
              ? item.notes || 'Nessuna nota aggiuntiva'
              : item.author || 'Autore non indicato'}
          </small>
          <em>
            {item.item_kind === 'text'
              ? 'Box vuoto'
              : item.is_proposal
                ? 'Non definitiva'
                : item.block_id
                  ? 'Blocco'
                  : 'Brano'}
          </em>
        </span>
        {item.item_kind !== 'text' && item.source_url && (
          <SongLinkPreview
            className="preview-tile"
            sourceUrl={item.source_url}
            title={item.title}
          />
        )}
      </article>
      {showNotes && item.item_kind !== 'text' && item.notes && (
        <p className="setlist-item-notes">{item.notes}</p>
      )}
    </div>
  );
}

function BranchPair({
  main,
  secondary,
  onPointerDown,
  onPointerMove,
  onPointerEnd,
}: {
  main: Item;
  secondary: Item;
  onPointerDown: (event: ReactPointerEvent, item: Item) => void;
  onPointerMove: (event: ReactPointerEvent) => void;
  onPointerEnd: () => void;
}) {
  return (
    <fieldset className="branch-pair">
      <legend>Bivio: due percorsi</legend>
      <span className="branch-binding" aria-hidden="true">
        <img src="/icons/bivio-b3.svg" alt="" />
      </span>
      {[main, secondary].map((item, index) => (
        <article
          className="branch-card"
          key={item.id}
          onPointerDown={(event) => onPointerDown(event, item)}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerEnd}
          onPointerCancel={onPointerEnd}
          onContextMenu={(event) => event.preventDefault()}
        >
          <small>{index === 0 ? 'Percorso principale' : 'Ramo'}</small>
          <strong>{item.title}</strong>
          <span>{item.author || 'Autore non indicato'}</span>
          {item.source_url && (
            <SongLinkPreview
              className="compact-player"
              sourceUrl={item.source_url}
              title={item.title}
            />
          )}
        </article>
      ))}
    </fieldset>
  );
}

function AlternativePair({
  main,
  alternative,
  onPointerDown,
  onPointerMove,
  onPointerEnd,
}: {
  main: Item;
  alternative: Item;
  onPointerDown: (event: ReactPointerEvent, item: Item) => void;
  onPointerMove: (event: ReactPointerEvent) => void;
  onPointerEnd: () => void;
}) {
  return (
    <fieldset className="alternative-pair">
      <legend>Brani alternativi</legend>
      <span className="alternative-binding" aria-hidden="true">
        <i />
        <img src="/icons/alternativa.svg" alt="" />
        <i />
      </span>
      {[main, alternative].map((item) => (
        <article
          className="branch-card alternative-card"
          key={item.id}
          onPointerDown={(event) => onPointerDown(event, item)}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerEnd}
          onPointerCancel={onPointerEnd}
          onContextMenu={(event) => event.preventDefault()}
        >
          <small>{item.id === main.id ? 'Principale' : 'Alternativa'}</small>
          <strong>{item.title}</strong>
          <span>{item.author || 'Autore non indicato'}</span>
          {item.source_url && (
            <SongLinkPreview
              className="compact-player"
              sourceUrl={item.source_url}
              title={item.title}
            />
          )}
        </article>
      ))}
    </fieldset>
  );
}

function SongLinkPreview({
  className,
  sourceUrl,
  title,
}: {
  className: 'preview-tile' | 'compact-player';
  sourceUrl: string;
  title: string;
}) {
  const videoId = youtubeVideoId(sourceUrl);
  return (
    <a
      className={`${className} ${videoId ? 'youtube-preview' : ''}`}
      href={sourceUrl}
      target="_blank"
      rel="noreferrer"
      aria-label={`Ascolta ${title}${videoId ? ' su YouTube' : ''}`}
      onPointerDown={(event) => event.stopPropagation()}
      onPointerUp={(event) => event.stopPropagation()}
      onClick={(event) => event.stopPropagation()}
    >
      {videoId && (
        <img
          src={`https://i.ytimg.com/vi/${encodeURIComponent(videoId)}/hqdefault.jpg`}
          alt=""
          loading="lazy"
        />
      )}
      <span className="player-play" aria-hidden="true">
        <VisualIcon name="play" />
      </span>
    </a>
  );
}

function SetlistMap({
  items,
  structures,
}: {
  items: Item[];
  structures: SetlistStructures;
}) {
  const fittedZoom = Math.max(0.48, Math.min(0.72, 5 / Math.max(items.length, 1)));
  const [zoom, setZoom] = useState(fittedZoom);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const drag = useRef<{ x: number; y: number; ox: number; oy: number } | null>(null);
  const branch = structures.branches[0];
  const byId = (id?: string) => items.find((item) => item.id === id);
  const origin = byId(branch?.originId);
  const originIndex = origin ? items.findIndex((item) => item.id === origin.id) : 0;
  const graphBaseY = 22 + Math.max(originIndex, 0) * 92;
  const mainItems = branch?.mainPathIds.map((id) => byId(id)).filter(Boolean) ?? [];
  const branchItems = branch?.branchPathIds.map((id) => byId(id)).filter(Boolean) ?? [];
  const alternative = structures.alternatives[0];
  const alternativeItem = byId(alternative?.alternativeId);
  const structuredNodes = [
    origin && { item: origin, x: 138, y: graphBaseY, kind: 'Origine' },
    ...mainItems.map((item, index) => ({
      item,
      x: 28,
      y: graphBaseY + 110 + index * 92,
      kind: 'Percorso A',
    })),
    ...branchItems.map((item, index) => ({
      item,
      x: 208,
      y: graphBaseY + 110 + index * 92,
      kind: 'Percorso B',
    })),
    alternativeItem && {
      item: alternativeItem,
      x: 28,
      y: graphBaseY + 110 + Math.max(mainItems.length, branchItems.length) * 92,
      kind: 'Alternativa',
    },
  ].filter(Boolean) as Array<{ item: Item; x: number; y: number; kind: string }>;
  const structuredIds = new Set(structuredNodes.map(({ item }) => item.id));
  const nodes = [
    ...items
      .filter((item) => !structuredIds.has(item.id))
      .map((item, index) => ({
        item,
        x: 118,
        y: 22 + index * 92,
        kind: `Brano ${item.position + 1}`,
      })),
    ...structuredNodes,
  ];
  const canvasHeight = Math.max(480, items.length * 92 + 110);
  return (
    <>
      <div className="map-toolbar">
        <button type="button" className="active">
          Vista Mappa
        </button>
        <span aria-hidden="true" />
        <button
          type="button"
          aria-label="Riduci"
          onClick={() => setZoom((value) => Math.max(0.55, value - 0.1))}
        >
          −
        </button>
        <button
          type="button"
          aria-label="Aumenta"
          onClick={() => setZoom((value) => Math.min(1.35, value + 0.1))}
        >
          ＋
        </button>
        <button
          type="button"
          onClick={() => {
            setZoom(fittedZoom);
            setOffset({ x: 0, y: 0 });
          }}
        >
          Adatta
        </button>
      </div>
      <div
        className="setlist-map"
        role="application"
        aria-label="Mappa strutturale della scaletta"
        onPointerDown={(event) => {
          if ((event.target as HTMLElement).closest('.map-node')) return;
          event.currentTarget.setPointerCapture?.(event.pointerId);
          drag.current = {
            x: event.clientX,
            y: event.clientY,
            ox: offset.x,
            oy: offset.y,
          };
        }}
        onPointerMove={(event) => {
          if (!drag.current) return;
          setOffset({
            x: drag.current.ox + event.clientX - drag.current.x,
            y: drag.current.oy + event.clientY - drag.current.y,
          });
        }}
        onPointerUp={() => {
          drag.current = null;
        }}
        onPointerCancel={() => {
          drag.current = null;
        }}
      >
        <div
          className="map-canvas"
          style={{
            height: canvasHeight,
            transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom})`,
          }}
        >
          {branch && origin && (
            <svg
              className="map-links"
              viewBox="0 0 360 480"
              aria-hidden="true"
              style={{ transform: `translateY(${graphBaseY - 22}px)` }}
            >
              <path
                className="branch-link"
                d="M180 94 V116 H70 V142 M180 116 H250 V142"
              />
              {mainItems[1] && <path className="branch-link" d="M70 210 V258" />}
              {branchItems[1] && <path className="branch-link" d="M250 210 V258" />}
              {alternativeItem && <path className="alternative-link" d="M70 326 V374" />}
            </svg>
          )}
          {nodes.map(({ item, x, y, kind }) => (
            <button
              type="button"
              key={`${kind}-${item.id}`}
              className={`map-node ${kind === 'Origine' ? 'origin' : ''} ${kind === 'Alternativa' ? 'alternative' : ''}`}
              style={{ left: x, top: y }}
            >
              <small>{kind}</small>
              <strong>{item.title}</strong>
              <span>{item.author}</span>
            </button>
          ))}
          {origin && (
            <span className="map-branch-mark" style={{ top: graphBaseY + 83 }}>
              <img src="/icons/bivio-b3.svg" alt="" />
            </span>
          )}
        </div>
      </div>
    </>
  );
}
function LibraryCard({
  song,
  selected,
  highlighted,
  onSelect,
  onPointerDown,
  onPointerMove,
  onPointerEnd,
}: {
  song: Song;
  selected: boolean;
  highlighted: boolean;
  onSelect: () => void;
  onPointerDown: (event: ReactPointerEvent<HTMLElement>) => void;
  onPointerMove: (event: ReactPointerEvent<HTMLElement>) => void;
  onPointerEnd: () => void;
}) {
  return (
    <article
      data-song-id={song.id}
      className={`library-card ${selected ? 'selected' : ''} ${highlighted ? 'just-added' : ''}`}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerEnd}
      onPointerCancel={onPointerEnd}
      onPointerLeave={onPointerEnd}
    >
      <div className="library-card-content">
        <input
          type="checkbox"
          checked={selected}
          onChange={onSelect}
          aria-label={`Seleziona ${song.title}`}
        />
        <span className={`library-selection-dot ${selected ? 'selected' : ''}`}>
          {selected ? '✓' : ''}
        </span>
        <span>
          <strong>{song.title}</strong>
          <small>{song.author || 'Autore non indicato'}</small>
        </span>
      </div>
    </article>
  );
}

function LibraryActionSheet({
  song,
  canAdd,
  onClose,
  onSend,
  onEdit,
  onAdd,
  onDelete,
}: {
  song: Song;
  canAdd: boolean;
  onClose: () => void;
  onSend: () => void;
  onEdit: () => void;
  onAdd: () => void;
  onDelete: () => void;
}) {
  return (
    <Sheet
      title={song.title}
      eyebrow="Archivio brani"
      onClose={onClose}
      className="context-actions-overlay"
    >
      <div className="action-grid library-action-grid">
        <button type="button" onClick={onSend}>
          <b>↗</b>
          <span>Invia</span>
        </button>
        <button type="button" onClick={onEdit}>
          <b>
            <VisualIcon name="edit" />
          </b>
          <span>Modifica</span>
        </button>
        <button type="button" onClick={onAdd} disabled={!canAdd}>
          <b>＋</b>
          <span>Aggiungi alla scaletta</span>
        </button>
        <button type="button" className="delete-action" onClick={onDelete}>
          <b>
            <VisualIcon name="trash" />
          </b>
          <span>Cancella</span>
        </button>
      </div>
    </Sheet>
  );
}

function SongDestinationSheet({
  setlists,
  onClose,
  onChoose,
}: {
  setlists: Setlist[];
  onClose: () => void;
  onChoose: (setlistId: string) => Promise<void>;
}) {
  return (
    <Sheet title="Scegli la scaletta" eyebrow="Aggiungi brano" onClose={onClose}>
      <div className="destination-list">
        {setlists.map((setlist) => (
          <button
            type="button"
            key={setlist.id}
            onClick={() => void onChoose(setlist.id)}
          >
            <span>
              <strong>{setlist.title}</strong>
              <small>{setlist.group_name}</small>
            </span>
            <span>›</span>
          </button>
        ))}
      </div>
    </Sheet>
  );
}

function Menu({
  onClose,
  onNavigate,
  onOpen,
  canManageSetlist,
  onPrint,
  onLeave,
  onDelete,
  onLogout,
}: {
  onClose: () => void;
  onNavigate: (view: AppView) => void;
  onOpen: (name: SheetName) => void;
  canManageSetlist: boolean;
  onPrint: () => void;
  onLeave: () => Promise<void>;
  onDelete: () => Promise<void>;
  onLogout: () => void;
}) {
  return (
    <div
      className="menu-overlay"
      onPointerDown={(event) => event.target === event.currentTarget && onClose()}
    >
      <aside className="menu-panel">
        <header>
          <div>
            <p>Jammo</p>
            <h2>{canManageSetlist ? 'Scaletta' : 'Menu'}</h2>
          </div>
          <button type="button" className="icon-button" onClick={onClose}>
            ×
          </button>
        </header>
        {!canManageSetlist && (
          <section>
            <h3>Navigazione</h3>
            <button type="button" onClick={() => onNavigate('setlists')}>
              <span>≡</span>Le mie scalette
            </button>
            <button type="button" onClick={() => onNavigate('library')}>
              <span>♫</span>Archivio brani
            </button>
            <button type="button" onClick={() => onNavigate('inbox')}>
              <span>↙</span>Inbox
            </button>
            <button type="button" onClick={() => onOpen('collaborators')}>
              <span>♧</span>Collaboratori
            </button>
          </section>
        )}
        {canManageSetlist && (
          <section>
            <h3>Questa scaletta</h3>
            <button type="button" onClick={() => onOpen('invite')}>
              <span>＋</span>Invita collaboratore
            </button>
            <button type="button" onClick={() => onOpen('rename-setlist')}>
              <span>✎</span>Rinomina scaletta
            </button>
            <button type="button" onClick={() => onOpen('rename-group')}>
              <span>♧</span>Rinomina gruppo
            </button>
            <button type="button" className="print-menu-action" onClick={onPrint}>
              <span aria-hidden="true">PDF</span>Stampa scaletta
            </button>
            <button type="button" onClick={() => void onLeave()}>
              <span>↪</span>Esci dalla scaletta
            </button>
            <div className="danger-zone">
              <small>Zona pericolosa</small>
              <button type="button" onClick={() => void onDelete()}>
                <span>×</span>Cancella scaletta per tutti
              </button>
            </div>
          </section>
        )}
        <section>
          <h3>Aiuto</h3>
          <button type="button" onClick={() => onOpen('guide')}>
            <span>?</span>Guida rapida
          </button>
        </section>
        {!canManageSetlist && (
          <section>
            <h3>Account</h3>
            <button type="button" onClick={() => onOpen('profile')}>
              <span>○</span>Profilo
            </button>
            <button type="button" onClick={onLogout}>
              <span>↪</span>Esci da questo dispositivo
            </button>
          </section>
        )}
      </aside>
    </div>
  );
}

function GuideSheet({ onClose }: { onClose: () => void }) {
  return (
    <Sheet title="Guida rapida" eyebrow="Come usare Jammo" onClose={onClose}>
      <div className="guide-content">
        <section>
          <h3>Archivio brani</h3>
          <p>
            Cerca per titolo o autore. Tocca un brano per selezionarlo; tienilo premuto
            per modificarlo, aggiungerlo a una scaletta o inviarlo.
          </p>
        </section>
        <section>
          <h3>Scaletta</h3>
          <p>
            Tieni premuto un box per aprire le azioni: modifica, sposta, inserisci un box
            vicino o crea collegamenti tra i brani.
          </p>
        </section>
        <section>
          <h3>Note</h3>
          <p>
            Le note si aggiungono modificando il box. L’interruttore Note nella scaletta
            le mostra o le nasconde senza cancellarle.
          </p>
        </section>
        <section>
          <h3>Collaborazione</h3>
          <p>
            Dal menu della scaletta puoi invitare collaboratori. Le modifiche alla
            scaletta sono condivise con tutti i partecipanti.
          </p>
        </section>
      </div>
    </Sheet>
  );
}

function CreateSetlist({
  onClose,
  onCreate,
  onDone,
}: {
  onClose: () => void;
  onCreate: (groupName: string, title: string) => Promise<boolean>;
  onDone: () => Promise<void>;
}) {
  const [title, setTitle] = useState('');
  const [groupName, setGroupName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    const created = await onCreate(groupName.trim(), title.trim());
    setBusy(false);
    if (!created) setError('Creazione non completata.');
    else await onDone();
  }
  return (
    <Sheet title="Nuova scaletta" eyebrow="Le mie scalette" onClose={onClose}>
      <form className="sheet-form" onSubmit={submit}>
        <label>
          Nome gruppo
          <input
            value={groupName}
            onChange={(event) => setGroupName(event.target.value)}
            placeholder="Es. The Blue Notes"
            maxLength={120}
            required
          />
        </label>
        <label>
          Nome della scaletta
          <input
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            placeholder="Es. Scaletta Estate"
            maxLength={120}
            required
          />
        </label>
        {error && <p className="form-error">{error}</p>}
        <div className="form-actions">
          <button type="button" onClick={onClose}>
            Annulla
          </button>
          <button
            type="submit"
            className="primary"
            disabled={busy || groupName.trim().length < 2 || title.trim().length < 2}
          >
            {busy ? 'Creazione…' : 'Crea'}
          </button>
        </div>
      </form>
    </Sheet>
  );
}

function RenameSharedName({
  title,
  label,
  currentValue,
  onClose,
  onRename,
}: {
  title: string;
  label: string;
  currentValue: string;
  onClose: () => void;
  onRename: (value: string) => Promise<boolean>;
}) {
  const [value, setValue] = useState(currentValue);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const normalized = value.trim();
  return (
    <Sheet title={title} eyebrow="Nome condiviso" onClose={onClose}>
      <form
        className="sheet-form"
        onSubmit={async (event) => {
          event.preventDefault();
          setBusy(true);
          setError('');
          const renamed = await onRename(normalized);
          setBusy(false);
          if (!renamed)
            setError('Rinomina non completata. Il nome precedente resta valido.');
        }}
      >
        <label>
          {label}
          <input
            value={value}
            onChange={(event) => setValue(event.target.value)}
            maxLength={120}
            required
          />
        </label>
        <small>Il nuovo nome sarà visibile a tutti i membri della scaletta.</small>
        {error && <p className="form-error">{error}</p>}
        <div className="form-actions">
          <button type="button" onClick={onClose}>
            Annulla
          </button>
          <button
            type="submit"
            className="primary"
            disabled={busy || normalized.length < 2 || normalized === currentValue}
          >
            {busy ? 'Salvataggio…' : 'Salva nome'}
          </button>
        </div>
      </form>
    </Sheet>
  );
}

function RenameSetlist({
  currentTitle,
  onClose,
  onRename,
}: {
  currentTitle: string;
  onClose: () => void;
  onRename: (title: string) => Promise<boolean>;
}) {
  const [title, setTitle] = useState(currentTitle);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  return (
    <Sheet title="Rinomina scaletta" eyebrow="Nome condiviso" onClose={onClose}>
      <form
        className="sheet-form"
        onSubmit={async (event) => {
          event.preventDefault();
          setBusy(true);
          setError('');
          const renamed = await onRename(title.trim());
          setBusy(false);
          if (!renamed)
            setError('Rinomina non completata. Il nome precedente resta valido.');
        }}
      >
        <label>
          Nome della scaletta
          <input
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            maxLength={120}
            required
          />
        </label>
        <small>Il nuovo nome sarà visibile a tutti i membri della scaletta.</small>
        {error && <p className="form-error">{error}</p>}
        <div className="form-actions">
          <button type="button" onClick={onClose}>
            Annulla
          </button>
          <button
            type="submit"
            className="primary"
            disabled={busy || title.trim().length < 2 || title.trim() === currentTitle}
          >
            {busy ? 'Salvataggio…' : 'Salva nome'}
          </button>
        </div>
      </form>
    </Sheet>
  );
}

function SongForm({
  song,
  requireDestination,
  setlists,
  onClose,
  onSave,
}: {
  song: Song | null;
  requireDestination: boolean;
  setlists: Setlist[];
  onClose: () => void;
  onSave: (
    id: string | null,
    title: string,
    author: string,
    url: string,
    destination?: { type: 'library' } | { type: 'setlist'; setlistId: string },
  ) => Promise<void>;
}) {
  const [title, setTitle] = useState(song?.title ?? '');
  const [author, setAuthor] = useState(song?.author ?? '');
  const [url, setUrl] = useState(song?.source_url ?? '');
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState<'form' | 'destination'>('form');
  const [destination, setDestination] = useState<'library' | 'setlist' | ''>('');
  const [setlistId, setSetlistId] = useState('');
  return (
    <Sheet
      title={song ? 'Modifica brano' : 'Aggiungi brano'}
      eyebrow="Archivio brani"
      onClose={onClose}
      centered
    >
      {step === 'destination' ? (
        <div className="destination-step">
          <h3>Dove vuoi aggiungere il brano?</h3>
          <label>
            <input
              type="radio"
              name="destination"
              checked={destination === 'library'}
              onChange={() => setDestination('library')}
            />
            <span>
              <strong>Archivio brani</strong>
              <small>Salva soltanto nel tuo archivio.</small>
            </span>
          </label>
          <label>
            <input
              type="radio"
              name="destination"
              checked={destination === 'setlist'}
              onChange={() => setDestination('setlist')}
            />
            <span>
              <strong>Una scaletta</strong>
              <small>Inserisci il brano in fondo.</small>
            </span>
          </label>
          {destination === 'setlist' && (
            <label className="destination-picker">
              Scegli la scaletta
              <select
                value={setlistId}
                onChange={(event) => setSetlistId(event.target.value)}
              >
                <option value="">Seleziona…</option>
                {setlists.map((entry) => (
                  <option key={entry.id} value={entry.id}>
                    {entry.title}
                  </option>
                ))}
              </select>
            </label>
          )}
          <div className="form-actions">
            <button type="button" onClick={() => setStep('form')}>
              Indietro
            </button>
            <button
              type="button"
              className="primary"
              disabled={!destination || (destination === 'setlist' && !setlistId) || busy}
              onClick={async () => {
                setBusy(true);
                await onSave(
                  null,
                  title.trim(),
                  author.trim(),
                  url.trim(),
                  destination === 'library'
                    ? { type: 'library' }
                    : { type: 'setlist', setlistId },
                );
                setBusy(false);
              }}
            >
              Conferma
            </button>
          </div>
        </div>
      ) : (
        <form
          className="sheet-form"
          onSubmit={async (event) => {
            event.preventDefault();
            if (requireDestination) setStep('destination');
            else {
              setBusy(true);
              await onSave(song?.id ?? null, title.trim(), author.trim(), url.trim());
              setBusy(false);
            }
          }}
        >
          <label>
            Link YouTube, Spotify o altro
            <input
              type="url"
              value={url}
              onChange={(event) => setUrl(event.target.value)}
              placeholder="https://…"
            />
          </label>
          <label>
            Titolo
            <input
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder="Titolo del brano"
              required
            />
          </label>
          <label>
            Autore
            <input
              value={author}
              onChange={(event) => setAuthor(event.target.value)}
              placeholder="Autore o interprete"
            />
          </label>
          <div className="form-actions">
            <button type="button" onClick={onClose}>
              Annulla
            </button>
            <button
              type="submit"
              className="primary"
              disabled={busy || title.trim().length < 1}
            >
              {busy ? 'Salvataggio…' : requireDestination ? 'Continua' : 'Salva'}
            </button>
          </div>
        </form>
      )}
    </Sheet>
  );
}

function SetlistItemForm({
  item,
  onClose,
  onSave,
}: {
  item: Item;
  onClose: () => void;
  onSave: (title: string, author: string, url: string, notes: string) => Promise<void>;
}) {
  const [title, setTitle] = useState(item.title);
  const [author, setAuthor] = useState(item.author ?? '');
  const [url, setUrl] = useState(item.source_url ?? '');
  const [notes, setNotes] = useState(item.notes ?? '');
  const [busy, setBusy] = useState(false);
  return (
    <Sheet
      title={item.block_id ? 'Modifica blocco' : 'Modifica brano'}
      eyebrow="Scaletta"
      onClose={onClose}
    >
      <form
        className="sheet-form"
        onSubmit={async (event) => {
          event.preventDefault();
          setBusy(true);
          await onSave(title.trim(), author.trim(), url.trim(), notes.trim());
          setBusy(false);
        }}
      >
        <label>
          Titolo
          <input
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            required
          />
        </label>
        <label>
          Autore
          <input value={author} onChange={(event) => setAuthor(event.target.value)} />
        </label>
        <label>
          Collegamento
          <input
            type="url"
            value={url}
            onChange={(event) => setUrl(event.target.value)}
          />
        </label>
        {item.item_kind !== 'text' && (
          <label>
            Note
            <textarea
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              placeholder="Indicazioni per questa scaletta"
              rows={3}
            />
          </label>
        )}
        <div className="form-actions">
          <button type="button" onClick={onClose}>
            Annulla
          </button>
          <button type="submit" className="primary" disabled={busy || !title.trim()}>
            {busy ? 'Salvataggio…' : 'Salva'}
          </button>
        </div>
      </form>
    </Sheet>
  );
}

function InsertBoxSheet({
  songs,
  onClose,
  onEmpty,
  onSong,
}: {
  songs: Song[];
  onClose: () => void;
  onEmpty: (text: string, notes: string) => Promise<boolean>;
  onSong: (songId: string) => Promise<boolean>;
}) {
  const [mode, setMode] = useState<'choose' | 'empty' | 'list'>('choose');
  const [text, setText] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [selectedSongId, setSelectedSongId] = useState('');
  const [insertError, setInsertError] = useState('');
  const back = () => setMode('choose');
  return (
    <Sheet title="Inserisci box" eyebrow="Dopo il box selezionato" onClose={onClose}>
      {mode === 'choose' && (
        <div className="insert-box-choices">
          <button type="button" onClick={() => setMode('empty')}>
            <strong>Box vuoto</strong>
            <small>Testo principale e note per i musicisti</small>
          </button>
          <button type="button" onClick={() => setMode('list')}>
            <strong>Brano dall’archivio</strong>
            <small>Scegli un brano dal tuo Archivio</small>
          </button>
        </div>
      )}
      {mode === 'empty' && (
        <form
          className="sheet-form"
          onSubmit={async (event) => {
            event.preventDefault();
            setBusy(true);
            await onEmpty(text.trim(), notes.trim());
            setBusy(false);
          }}
        >
          <label>
            Testo principale
            <input
              value={text}
              onChange={(event) => setText(event.target.value)}
              placeholder="Es. PAUSA"
              required
            />
          </label>
          <label>
            Note per i musicisti
            <textarea
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              placeholder="Indicazioni facoltative"
            />
          </label>
          <div className="form-actions">
            <button type="button" onClick={back}>
              Indietro
            </button>
            <button type="submit" className="primary" disabled={busy || !text.trim()}>
              {busy ? 'Inserimento…' : 'Inserisci'}
            </button>
          </div>
        </form>
      )}
      {mode === 'list' && (
        <div className="insert-song-list">
          {songs.length === 0 ? (
            <p className="sheet-copy">L’Archivio brani è vuoto.</p>
          ) : (
            songs.map((song) => (
              <button
                type="button"
                key={song.id}
                className={selectedSongId === song.id ? 'selected' : ''}
                aria-pressed={selectedSongId === song.id}
                onClick={() => {
                  if (!busy) {
                    setSelectedSongId(song.id);
                    setInsertError('');
                  }
                }}
              >
                <span>
                  <strong>{song.title}</strong>
                  <small>{song.author || 'Autore non indicato'}</small>
                  {selectedSongId === song.id && <em>Selezionato</em>}
                </span>
                <b aria-hidden="true" className="selection-check">
                  {selectedSongId === song.id ? '✓' : ''}
                </b>
              </button>
            ))
          )}
          {insertError && <p className="form-error insert-error">{insertError}</p>}
          <div className="form-actions">
            <button type="button" onClick={back}>
              Indietro
            </button>
            <button
              type="button"
              className="primary"
              disabled={busy || !selectedSongId}
              onClick={async () => {
                setInsertError('');
                setBusy(true);
                try {
                  const inserted = await onSong(selectedSongId);
                  if (!inserted)
                    setInsertError(
                      'Inserimento non completato. Il brano resta selezionato: riprova oppure torna indietro.',
                    );
                } catch {
                  setInsertError(
                    'Inserimento non completato. Il brano resta selezionato: riprova oppure torna indietro.',
                  );
                } finally {
                  setBusy(false);
                }
              }}
            >
              {busy ? 'Inserimento…' : 'Inserisci'}
            </button>
          </div>
        </div>
      )}
    </Sheet>
  );
}

function ActionSheet({
  item,
  ordinal,
  onClose,
  onMove,
  onEdit,
  onInsert,
  canLock,
  chainActive,
  branchActive,
  alternativeActive,
  proposalActive,
  onLock,
  onBranch,
  onAlternative,
  onProposal,
  onRemove,
}: {
  item: Item;
  ordinal: number;
  onClose: () => void;
  onMove: () => void;
  onEdit: () => void;
  onInsert: () => void;
  canLock: boolean;
  chainActive: boolean;
  branchActive: boolean;
  alternativeActive: boolean;
  proposalActive: boolean;
  onLock: () => void;
  onBranch: () => void;
  onAlternative: () => void;
  onProposal: () => void;
  onRemove: () => Promise<void>;
}) {
  const icons = {
    edit: 'edit',
    move: 'move',
    remove: 'trash',
    branch: 'branch',
    lock: 'chain',
    alternative: 'alternative',
  } as const;
  return (
    <Sheet
      title={item.title}
      eyebrow={`Brano ${ordinal}`}
      onClose={onClose}
      className="context-actions-overlay"
    >
      <div className="action-grid">
        {songContextActions(!!item.block_id)
          .filter((action) => action.id !== 'remove')
          .sort(
            (first, second) =>
              ['move', 'edit', 'lock', 'branch', 'alternative'].indexOf(first.id) -
              ['move', 'edit', 'lock', 'branch', 'alternative'].indexOf(second.id),
          )
          .map((action) => (
            <button
              type="button"
              key={action.id}
              disabled={!action.enabled || (action.id === 'lock' && !canLock)}
              onClick={
                action.id === 'move'
                  ? onMove
                  : action.id === 'edit'
                    ? onEdit
                    : action.id === 'lock'
                      ? onLock
                      : action.id === 'branch'
                        ? onBranch
                        : action.id === 'alternative'
                          ? onAlternative
                          : undefined
              }
            >
              <b>
                {action.id === 'lock' ? (
                  <img
                    src="/icons/catena.svg"
                    alt=""
                    className="action-structure-icon chain"
                  />
                ) : action.id === 'branch' ? (
                  <img
                    src="/icons/bivio-b3.svg"
                    alt=""
                    className="action-structure-icon"
                  />
                ) : action.id === 'alternative' ? (
                  <img
                    src="/icons/alternativa.svg"
                    alt=""
                    className="action-structure-icon"
                  />
                ) : (
                  <VisualIcon name={icons[action.id]} />
                )}
              </b>
              <span>
                {action.id === 'lock' && chainActive
                  ? 'Togli blocco'
                  : action.id === 'branch' && branchActive
                    ? 'Togli Bivio'
                    : action.id === 'alternative' && alternativeActive
                      ? 'Togli alternativa'
                      : action.label}
              </span>
              {action.id === 'lock' && !canLock && !chainActive && (
                <small>Richiede un blocco successivo</small>
              )}
            </button>
          ))}
        <button type="button" onClick={onInsert}>
          <b aria-hidden="true">＋</b>
          <span>Inserisci box</span>
        </button>
        <button
          type="button"
          className={proposalActive ? 'proposal-action active' : 'proposal-action'}
          onClick={onProposal}
        >
          <b aria-hidden="true">◆</b>
          <span>{proposalActive ? 'Approva' : 'Proposta'}</span>
          <small>
            {proposalActive
              ? 'Rendi il blocco definitivo'
              : 'Contrassegna come non definitiva'}
          </small>
        </button>
      </div>
      <button
        type="button"
        className="delete-action delete-action-wide"
        onClick={() => void onRemove()}
      >
        <b>
          <VisualIcon name="trash" />
        </b>
        <span>Cancella blocco</span>
      </button>
    </Sheet>
  );
}

function ConfirmDialog({
  title,
  copy,
  danger,
  confirmLabel,
  cancelLabel,
  onCancel,
  onConfirm,
}: {
  title: string;
  copy: string;
  danger?: boolean;
  confirmLabel?: string;
  cancelLabel?: string;
  onCancel: () => void;
  onConfirm: () => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  return (
    <div className="overlay centered confirm-overlay" role="presentation">
      <section
        className="confirm-dialog"
        role="alertdialog"
        aria-modal="true"
        aria-label={title}
      >
        <span className={`confirm-symbol ${danger ? 'danger' : ''}`}>
          <VisualIcon name={danger ? 'trash' : 'chain'} />
        </span>
        <h2>{title}</h2>
        <p>{copy}</p>
        <div className="form-actions">
          <button type="button" onClick={onCancel}>
            {cancelLabel ?? 'Mantieni'}
          </button>
          <button
            type="button"
            className={danger ? 'danger-button' : 'primary'}
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              await onConfirm();
              setBusy(false);
            }}
          >
            {busy
              ? 'Attendi…'
              : (confirmLabel ?? (danger ? 'Cancella per tutti' : 'Conferma'))}
          </button>
        </div>
      </section>
    </div>
  );
}

function InviteSheet({
  setlistTitle,
  onClose,
  onInvite,
  pendingInvites,
  onRevoke,
}: {
  setlistTitle: string;
  onClose: () => void;
  onInvite: () => Promise<boolean>;
  pendingInvites: PendingInvite[];
  onRevoke: (inviteId: string) => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  return (
    <Sheet title="Invita collaboratore" eyebrow={setlistTitle} onClose={onClose}>
      <form
        className="sheet-form invite-form"
        onSubmit={async (event) => {
          event.preventDefault();
          setBusy(true);
          await onInvite();
          setBusy(false);
        }}
      >
        <p className="sheet-copy">
          Crea un link personale per questa scaletta. Il primo destinatario che lo usa
          inserisce il proprio numero e lo strumento, poi il link viene consumato.
        </p>
        <p className="sheet-copy">
          Il link è monouso, revocabile e valido per 7 giorni. Condividilo soltanto con la
          persona che vuoi invitare.
        </p>
        <div className="form-actions">
          <button type="button" onClick={onClose}>
            Annulla
          </button>
          <button type="submit" className="primary" disabled={busy}>
            {busy ? 'Preparazione…' : 'Condividi invito'}
          </button>
        </div>
      </form>
      {pendingInvites.length > 0 && (
        <div className="collaborator-list">
          {pendingInvites.map((invite) => (
            <article className="collaborator-card" key={invite.id}>
              <div>
                <strong>Invito rapido in attesa</strong>
                <small>
                  Scade il {new Date(invite.expires_at).toLocaleDateString('it-IT')}
                </small>
              </div>
              <button type="button" onClick={() => void onRevoke(invite.id)}>
                Revoca
              </button>
            </article>
          ))}
        </div>
      )}
    </Sheet>
  );
}
function CollaboratorsSheet({
  collaborators,
  recentIds,
  selectedCount,
  onSend,
  onRemove,
  onClose,
}: {
  collaborators: Collaborator[];
  recentIds: string[];
  selectedCount: number;
  onSend: (profileId: string) => Promise<void>;
  onRemove: (profileId: string) => Promise<void>;
  onClose: () => void;
}) {
  const [query, setQuery] = useState('');
  const filtered = filterCollaborators(collaborators, query);
  const { recent, others } = splitRecentCollaborators(filtered, recentIds);
  const card = (person: Collaborator) => (
    <article className="collaborator-card" key={person.profile_id}>
      <span>♫</span>
      <div>
        <strong>
          {memberLabel({ localContactName: person.display_name, phone: person.phone })}
        </strong>
        <small>{person.instrument_role}</small>
        <span className="collaborator-phone">{formatItalianPhone(person.phone)}</span>
      </div>
      <button
        type="button"
        disabled={selectedCount === 0}
        onClick={() => onSend(person.profile_id)}
      >
        Invia {selectedCount || ''}
      </button>
      {selectedCount === 0 && (
        <button type="button" onClick={() => void onRemove(person.profile_id)}>
          Rimuovi
        </button>
      )}
    </article>
  );
  return (
    <Sheet title="Collaboratori" eyebrow="Le tue collaborazioni" onClose={onClose}>
      <label className="collaborator-search">
        <span>Cerca collaboratore</span>
        <input
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Numero o strumento"
        />
      </label>
      {collaborators.length === 0 ? (
        <EmptyState icon="♧" title="Nessun collaboratore">
          Compariranno soltanto le persone con cui condividi una scaletta.
        </EmptyState>
      ) : (
        <>
          <div className="collaborator-list">{others.map(card)}</div>
          {recent.length > 0 && (
            <section className="recent-collaborators">
              <h3>Recenti</h3>
              <div className="collaborator-list">{recent.map(card)}</div>
            </section>
          )}
          {filtered.length === 0 && (
            <p className="compact-empty">
              Nessun collaboratore corrisponde alla ricerca.
            </p>
          )}
        </>
      )}
    </Sheet>
  );
}

function ProfileSheet({
  profile,
  onClose,
  onSave,
}: {
  profile: { display_name: string; instrument_role: string; phone: string | null };
  onClose: () => void;
  onSave: (displayName: string, instrument: string) => Promise<void>;
}) {
  const legacyPhoneName =
    profile.phone !== null &&
    profile.display_name.replace(/\D/g, '') === profile.phone.replace(/\D/g, '');
  const [displayName, setDisplayName] = useState(
    legacyPhoneName ? '' : profile.display_name,
  );
  const [instrument, setInstrument] = useState(profile.instrument_role);
  return (
    <Sheet title="Profilo" eyebrow="Account" onClose={onClose}>
      <div className="profile-card">
        <span>♫</span>
        <div>
          <strong>{profile.display_name}</strong>
          <small>{profile.instrument_role}</small>
          {profile.phone && <small>{formatItalianPhone(profile.phone)}</small>}
        </div>
      </div>
      <form
        className="sheet-form"
        onSubmit={async (event) => {
          event.preventDefault();
          await onSave(displayName.trim(), instrument.trim());
        }}
      >
        <label>
          Numero di telefono
          <input
            value={profile.phone ? formatItalianPhone(profile.phone) : ''}
            inputMode="tel"
            readOnly
            aria-readonly="true"
          />
        </label>
        <label>
          Nome visualizzato
          <input
            value={displayName}
            onChange={(event) => setDisplayName(event.target.value)}
            autoComplete="name"
            placeholder="Inserisci il tuo nome"
            required
          />
        </label>
        <label>
          Strumento musicale
          <input
            value={instrument}
            onChange={(event) => setInstrument(event.target.value)}
            required
          />
        </label>
        <small>
          Nome e strumento sono visibili agli altri membri. Il numero identifica il
          musicista.
        </small>
        <button type="submit" className="primary">
          Salva profilo
        </button>
      </form>
    </Sheet>
  );
}

function friendlyError(message: string) {
  if (message.includes('last member cannot leave'))
    return 'Sei l’ultimo membro: puoi mantenere la scaletta oppure cancellarla.';
  if (message.includes('membership required'))
    return 'Questa azione richiede di essere membri della scaletta.';
  if (message.includes('invalid block'))
    return 'Per creare un blocco scegli almeno due brani.';
  if (message.includes('collaboration required'))
    return 'Puoi inviare brani soltanto a un collaboratore.';
  return 'Non è stato possibile completare l’operazione. Riprova.';
}
